import { useRouter } from 'next/router';
import {
  convertTemplateToHtml,
  documentThumbnailUrl,
  fakeAtVariables,
  getTextFromHtml,
  isTRPCClientError,
  joinIds,
  splitIds,
  updateWithToast,
  updateWithToastPromise,
} from '@/clientOnlyUtils/clientUtils';
import { getEditorDocuments } from '@/clientOnlyUtils/quillUtils';
import { Toast } from '@/clientOnlyUtils/toast';
import * as Sentry from '@sentry/nextjs';
import { getAuth } from 'firebase/auth';
import React, { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { z } from 'zod';

import type {
  ActionSectionData,
  ActionStep,
  ActionStepConfig,
  AiChoice,
  AIMessageSectionData,
  CadenceGraph,
  ContactDetailMutations,
  ContactDetailPageCoreProps,
  ContactDetailData as CoreContactDetailData,
  EditorDocument,
  HistoryData,
  OwnerOption,
  ContactFieldName as PureContactFieldName,
  ReminderCardCallbacks,
  ReminderCardData,
  SendMode,
  StagingCandidate,
  StepChanges,
  StepData,
  StepEditorCallbacks,
  StepEditorData,
  StepType,
  TemplateOption,
  VoiceNoteCardCallbacks,
  VoiceNoteCardData,
} from '@poseidon/components/ContactDetail';
import type CadenceStepType from '@poseidon/types/pg-gen/cadenceStepType';
import type ContactFieldName from '@poseidon/types/pg-gen/contactFieldName';
import type MailType from '@poseidon/types/pg-gen/mailType';
import type PriceTierEnum from '@poseidon/types/pg-gen/priceTierEnum';
import type ProfileLinkType from '@poseidon/types/pg-gen/profileLinkType';
import type SendMessageType from '@poseidon/types/pg-gen/sendMessageType';
import type UserTeamStatus from '@poseidon/types/pg-gen/userTeamStatus';
import type { IntegrationStatus } from '@poseidon/types/types/types';
import { type RouterInputs, type RouterOutputs } from '@poseidon/api/types';
import { Avatar } from '@poseidon/components/Avatar';
import { convertAtVariablesToTipTap, convertDocumentsToTipTap } from '@poseidon/components/CadenceStep';
import {
  ContactDetailPageCore,
  ContactDetailPageNotFound,
  ContactDetailPageSkeleton,
  defaultFormattedLabels,
} from '@poseidon/components/ContactDetail';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import SiftConditionBuilderCompact from '@poseidon/components/SiftConditionBuilder/SiftConditionBuilderCompact';
import { CALL_OUTCOMES, WRONG_NUMBER_MARKED_TOAST } from '@poseidon/components/Softphone/CallOutcomePicker';
import { cadenceStepBuildGraph } from '@poseidon/shared-utils/cadenceStep';
import { svrExtractAtVariables } from '@poseidon/shared-utils/message';
import { cadenceBlockingStateValues } from '@poseidon/types/types/cadenceState.type';
import { contactEmailFieldKeySchema, contactPrimarySettableFieldKeySchema } from '@poseidon/types/types/contact.type';

import { useAiStepBulkCreateMutation } from '@/hooks/trpc/useAiStepMutation';
import {
  useContactApproveVoiceNoteStepMutation,
  useContactCreateVoiceNoteStepMutation,
  useContactGenerateVoiceNoteAudioMutation,
  useGenerateUploadUrlMutation,
  useMakePublicAudioFileUrlMutation,
} from '@/hooks/trpc/useAudioFileMutation';
import {
  useCadenceChangeStepStateMutation,
  useCadenceCreateStepMutation,
  useCadenceDeleteFutureStepsMutation,
  useCadenceDeleteMutation,
  useCadenceDeleteStepMutation,
  useCadencePauseAllMutation,
  useCadenceReattachMutation,
  useCadenceResumeAllMutation,
  useCadenceUpdateStepMutation,
  useChangeCadenceOwnerMutation,
} from '@/hooks/trpc/useCadenceMutation';
import { useContactCreateCadenceMutation } from '@/hooks/trpc/useCadenceTemplateMutation';
import { useContactActionMutations } from '@/hooks/trpc/useContactActionMutations';
import { useContactAckContentViewAndResponse, useEmailInteractionsSummary } from '@/hooks/trpc/useContactMutation';
import {
  useGlobalReminderChangeOwner,
  useGlobalReminderCreate,
  useGlobalReminderDelete,
  useGlobalReminderUpdate,
} from '@/hooks/trpc/useGlobalReminders';
import { useCreateReminderMutation } from '@/hooks/trpc/useReminderMutation';
import { useUnipileAuthMutation } from '@/hooks/trpc/useUserMutation';
import { useAsyncDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { isMacLike } from '@/hooks/useKey';
import { usePostMessageListener } from '@/hooks/usePostMessageListener';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import { useTouchEditorContext } from '@/hooks/useTouchEditorContext';
import { api } from '@/utils/api';
import { computeActionEditorSeed, isSeedableActionStepType } from '@/utils/contactActionSeed';
import { useAuthContext } from '~/auth/auth.context';
import { useEnrichmentStatus } from '@/hooks/trpc/useEnrichmentStatus';
import { cadenceStepsList, linkedinStepsList, otherStepsList } from '~/components/CadenceStep/components/data';
import { ClickToCallButton } from '~/components/ClickToCallButton';
import { CampaignDetailView, CampaignListView } from '~/components/ContactCampaignsSection';
import { RequiredActionsListView } from '~/components/ContactRequiredActionsSection/RequiredActionsListView';
import { ContactTagEditor } from '~/components/ContactTagEditor';
import type { LocalGraphNodes } from '~/components/CTGStep/CTGStep';
import type { StatusByTemplateStepId } from '~/components/TouchCampaignGraph/touchGraphLayout';
import { ScheduleCallButton } from '~/components/ScheduleCallButton';
import { ContactSequenceGraph } from '~/components/Sequence/ContactSequenceGraph';
import { ContactTouchGraphPanel } from '~/components/TouchCampaignGraph/ContactTouchGraphPanel';
import {
  useAddForeignMappingMutation,
  useContactResetGenderMutation,
  useContactSetDisqualifiedMutation,
  useContactSetMinAgeOverrideMutation,
  useContactSetStageMutation,
  useContactSetTrackingMutation,
  useDeleteForeignMappingMutation,
  useDeleteMeetingBookedMutation,
  useEnhanceContactMutation,
  useMakeContactFieldPrimaryMutation,
  useMarkInteractionAsMeetingBookedMutation,
  useMarkPhoneFirmNumberMutation,
  useReassignContactPhoneToOrgMutation,
  useRegenerateDossierMutation,
  useRemoveContactFieldPrimaryMutation,
  useRemoveContactFieldValueMutation,
  useResearchFromLinkedInMutation,
  useUpdateContactMutation,
  useVerifyContactEmailMutation,
  useVerifyForeignMappingMutation,
} from '~/hooks/trpc/useContactMutation';
import { useOrgSetStageMutation } from '~/hooks/trpc/useOrgMutation';

type Document = { id: string; title: string; ogImageUrl: string | null; url: string; type?: string; imageUrl?: string };

const LINKEDIN_STEPS: ActionStepConfig[] = linkedinStepsList.map((s) => ({
  value: s.value as StepType,
  labelFull: defaultFormattedLabels[s.value] ?? s.labelFull,
  labelFullIfResponse: s.labelFullIfResponse,
  error: s.error,
}));

const OTHER_STEPS: ActionStepConfig[] = otherStepsList.map((s) => ({
  value: s.value as StepType,
  labelFull: defaultFormattedLabels[s.value] ?? s.labelFull,
  labelFullIfResponse: s.labelFullIfResponse,
  error: s.error,
}));

const idSchema = z.enum(['interact', 'data', 'action']);
export type ID = z.infer<typeof idSchema>;
export const convertStringToID = (s?: string): ID => idSchema.default('action').catch('action').parse(s);

type CadenceStepInput = RouterInputs['cadence']['createStep']['data'];
type CadenceAddStepV2Params = RouterInputs['cadence']['createStep']['data'];
type CadenceUpdateStepParams = RouterInputs['cadence']['updateStep']['data'];

/** Narrow the picker's string outcome to the completeCallStep mutation's disposition enum. */
type CompleteCallOutcome = RouterInputs['voiceCall']['completeCallStep']['outcome'];
const COMPLETE_CALL_OUTCOME_VALUES: readonly string[] = CALL_OUTCOMES.map((o) => o.value);
function isCompleteCallOutcome(value: string): value is CompleteCallOutcome {
  return COMPLETE_CALL_OUTCOME_VALUES.includes(value);
}

/** How AI steps are restricted — local mirror of the server union. */
type AiStepAllowedType = 'message' | 'connect' | 'inmail';

/**
 * A draft / respond step as this page constructs and renders it. Flat local
 * mirror of the server step DTO — built here and passed to the pure ContactDetail
 * components, which accept the same shape structurally.
 */
type Step = {
  id: string;
  type: StepType;
  subject: string | null;
  body2: string | null;
  to: string | null;
  from: string | null;
  cc: string | null;
  bcc: string | null;
  onlyOpenLink: boolean;
  useEmailSignature: boolean;
};

// Schema for parsing saved step form data from localStorage
const actionFormSchema = z
  .object({
    subject: z.string().optional(),
    body2: z.string().optional(),
    from: z.string().optional(),
    to: z.string().optional(),
    cc: z.string().optional(),
    bcc: z.string().optional(),
  })
  .catch({});

export type UserTeamData = {
  // base user + team identity
  userId: string;
  userEmail: string;
  isSuperAdmin: boolean;
  verified: boolean;
  attribution: string | null;
  teamId: string;
  teamName: string;
  teamPriceTier: PriceTierEnum;
  bcc: string | null;
  isAdmin: boolean;
  userTeamStatus: UserTeamStatus;
  profilePicture: string | null;
  userTeamName: string | null;
  userTeamTimezoneName: string | null;
  // SvrWUserTeamRunData
  onlyUseSalesNav: boolean;
  maxEmailOpens: number;
  mailType: MailType | null;
  sendMessageType: SendMessageType | null;
  useEmailSignatureByDefault: boolean;
  emailSignature: string | null;
  useContactEmailAsBcc: boolean;
  messageDelayStartSeconds: number;
  messageDelayEndSeconds: number;
  minCadenceRunDelay: number;
  activeUserLevel: number | null;
  openProfileLinksAs: ProfileLinkType | null;
  orgsToCheck: number;
  // SvrWTeamProfileData
  primaryColor: string;
  secondaryColor: string;
  createdAt: Date;
  teamTimezone: string | null;
  defaultStageId: string | null;
  interestDetectedStageId: string | null;
  docUrl: string | null;
  contentLibraryCountUserIps: boolean;
  ai_info: string | null;
  elevenlabs_collection_id: string | null;
  honorificAgeThreshold: number;
  aiStepAllowedTypes: AiStepAllowedType[];
  superadminReplyAlertsEnabled: boolean;
  voiceCallingEnabled: boolean;
  voiceProvider: 'telnyx' | 'twilio';
  // SvrWUserTeamProfileData
  userTeamEmail: string | null;
  calendarLink: string | null;
  calendarText: string | null;
  contactNumber: string | null;
  title: string | null;
  defaultReminderId: string | null;
  updatedOn: Date | null;
  workContext: string | null;
  niche: string | null;
  focus: string | null;
  tone: string | null;
  takeActionMessageInstructions: string | null;
  takeActionOnlyNewPeople: boolean;
  deleteCadenceAfterResponding: boolean;
  checkLiStartingNewPosition: boolean;
  lastAnalyticsScrape: Date | null;
  pexelsApiKey: string | null;
  replicateApiKey: string | null;
  anthropicApiKey: string | null;
  tavilyApiKey: string | null;
  tavilyApiError: string | null;
  unipileAccountId: string | null;
  unipileAccountError: string | null;
  elevenLabsVoiceId: string | null;
  isPostsParsingEnabled: boolean;
  videoScript: string | null;
  campaignOverrideMode: 'skip_existing' | 'force_override' | 'smart_override';
  liLoginVerificationSelector: string | null;
  serverExecutionEnabled?: boolean;
  autoFollowupEnabled?: boolean;
  followupDays?: number;
  maxFollowups?: number;
  campaignPrefilterEnabled: boolean;
  autoLoaderEnabled?: boolean;
  campaignSwitchEnabled: boolean;
  sequenceEnabled: boolean;
  touchCampaignEnabled: boolean;
  voiceEnabled: boolean;
  voiceCallerNumber: string | null;
  multiInboxEmailEnabled?: boolean;
  // SvrWTeamLogo
  teamLogo: { url: string; filename: string; webp?: string; width?: number; height?: number } | null;
  // linkedin account
  liPictureUrl: string | null;
  liPublicIdentifier: string | null;
  liFirstName: string | null;
  liLastName: string | null;
  liAccountConnected: boolean;
  liSubheading: string | null;
  hasSalesNav: boolean;
  liSalesNavTokensRemaining: number | null;
  liLinkedinConnectLimitReached: boolean | null;
  liLinkedinConnectLimitSyncDate: Date | null;
  liSalesNavTokensSyncDate: Date | null;
  // SvrWUserSettings
  sendNagEmails: boolean;
  allowTriggers: boolean | null;
  goalsAvailable: boolean;
  // SvrWUserLastUsedCadenceTemplate
  lastUsedCadenceTemplateId: string | null;
  lastUsedCadenceTemplateName: string | null;
  // SvrWTeamCredits
  credits: { monthAllowed: number; monthRemaining: number };
  // SvrWUserTeamCredits
  isPremium: boolean;
  creditsRemaining: number;
  // SvrWithTeamApiEnabledFlags (+ SvrWithTwilioFlags)
  twilioConnected: IntegrationStatus;
  twilioRecordCalls: boolean;
  twilioPhoneNumber: string | null;
  hubspotConnected: IntegrationStatus;
  slackConnected: IntegrationStatus;
  gtmConnected: IntegrationStatus;
  webhookConnected: IntegrationStatus;
  newsApiConnected: IntegrationStatus;
  // done-for-you bucket
  doneForYouBucketId: number | null;
  doneForYouLastRan: Date | null;
  doneForYouOrder: number | null;
  doneForYouId: number | null;
  hasUserAgent: boolean;
  hasLiAtCookie: boolean;
  // SvrWUserTeamSchedule
  scheduleIsConfigured: boolean;
  scheduleIsWithinSchedule: boolean;
  scheduleNextWindowStartMs: number | null;
  scheduleNextWindowStartFormatted: string | null;
};

// Use the exact type from the tRPC query output
export type ContactDetailData = NonNullable<RouterOutputs['contact']['byId']>;

// Type for tRPC history data
type TrpcHistoryData = NonNullable<RouterOutputs['history']['contact']>['data'];

/**
 * Maps tRPC history data to the pure HistoryData type for ContactInteractSectionPure
 */
function mapHistoryData(trpcHistory: TrpcHistoryData | undefined): HistoryData {
  if (!trpcHistory) return [];
  return trpcHistory.map((item) => ({
    interaction: {
      id: parseInt(item.interaction.id, 10) || 0,
      odooInteractionId: item.interaction.id, // UUID for retry attachment
      interactionAction: item.interaction.interactionAction,
      userId: item.interaction.userId,
      contactId: '',
      teamId: '',
      interactionType: item.interaction.interactionAction,
      createdAt: item.interaction.date,
      date: item.interaction.date,
      updatedOn: item.interaction.updatedOn,
      subject: item.interaction.subject,
      body: item.interaction.body,
      from: item.interaction.from,
      to: item.interaction.to,
      dataUrl: item.interaction.data_url,
      dataUrlType: item.interaction.data_url_type,
      transcription: item.interaction.transcription,
      transcription_model: item.interaction.transcription_model,
      transcription_status: item.interaction.transcription_status,
      transcription_error: item.interaction.transcription_error,
      meetingBookedFromThis: item.interaction.meetingBookedFromThis,
      data: null,
      liConversationUrl: null,
      liConversationId: null,
      cadenceId: null,
      orgId: null,
      stepId: null,
      linkViewId: null,
      documentId: null,
      globalReminderId: null,
    },
    interactionUser: item.interactionUser
      ? {
          userId: '',
          teamId: '',
          teamName: '',
          teamPriceTier: '',
          name: item.interactionUser.name,
          profilePicture: null,
          title: null,
          calendarLink: null,
          contactNumber: null,
          email: item.interactionUser.email,
          liConnected: null,
          liConnectedAt: null,
          liReceivedConnection: null,
          liReceivedConnectionAt: null,
          liSentConnection: null,
          liSentConnectionAt: null,
          liFollowed: null,
          liFollowedAt: null,
          emailCredentialId: null,
          disqualified: false,
          disqualifiedAt: null,
          meetingInterested: false,
        }
      : null,
    documentView: item.documentView
      ? {
          id: item.documentView.doc_id || '',
          docId: item.documentView.doc_id,
          title: item.documentView.title || '',
          url: '',
          ogImageUrl: item.documentView.og_image_url,
          description: item.documentView.description,
          fp_ip_loc_city_name: item.documentView.fp_ip_loc_city_name,
          fp_ip_loc_country_name: item.documentView.fp_ip_loc_country_name,
          fp_browser_name: item.documentView.fp_browser_name,
          fp_os: item.documentView.fp_os,
        }
      : null,
    contact: {
      id: '',
      teamId: '',
      orgId: null,
      stageId: null,
      tracked: true,
      lastEnrichedAt: null,
      unverified: false,
      notes: null,
      liId: null,
      liUsername: null,
      liSalesNavId: null,
      liHiring: null,
      liOpenToWork: null,
      liOpenLink: null,
      liPremium: null,
      liAbout: null,
      liAboutSummary: null,
      liConnectionsCount: null,
      liRecentPostsCount: null,
      liActiveUserProbability: null,
      fields: {},
      primaryFields: {
        firstName: {
          createdAt: new Date(),
          fieldName: 'firstName',
          source: '',
          data: item.contact.first_name || '',
          enrichSource: null,
          primary: true,
          verified: null,
          emailVerificationCode: null,
          confidenceScore: null,
        },
        lastName: {
          createdAt: new Date(),
          fieldName: 'lastName',
          source: '',
          data: item.contact.last_name || '',
          enrichSource: null,
          primary: true,
          verified: null,
          emailVerificationCode: null,
          confidenceScore: null,
        },
      },
      primaryPictureUrl: item.contact.picture_url,
      ageEstimate: null,
    },
    cadence: item.cadence
      ? {
          id: item.cadence.id ?? '',
          teamId: '',
          userId: '',
          contactId: '',
          cadenceTemplateId: '',
          name: item.cadence.name,
          paused: false,
          deletedAt: null,
          createdAt: new Date(),
          startTime: null,
        }
      : null,
  }));
}

export type ContactDetailPageNextJsProps = UserTeamData & {
  data: ContactDetailData | null;
  isLoading: boolean;

  // Navigation state
  detailType?: string;

  authTeamId: string;
  authUserId: string;
  propAuthIsSuperadmin?: boolean;
  // UI options
  orgFirst?: boolean;
  onClose?: () => void;
};

/**
 * States in which a cadence is still in-progress ("active"), so it belongs in the
 * header campaign switcher. Filtering by STATE (not the `isActive` flag) is
 * deliberate — per POS-2216 terminal cadences can be left `is_active = true`.
 */
const BLOCKING_STATES = new Set<string>(cadenceBlockingStateValues);

export const ContactDetailPageNextJs = ({
  data,
  isLoading,
  detailType: detailTypeProp,
  onClose,
  orgFirst,
  authTeamId,
  authUserId,
  propAuthIsSuperadmin,
  ...userTeam
}: ContactDetailPageNextJsProps) => {
  const defaultId = convertStringToID(detailTypeProp);

  // Respond step editor state
  const [respondStep, setRespondStep] = React.useState<Step | undefined>();
  const [respondChanges, setRespondChanges] = React.useState<StepChanges>({});
  const [respondAiChoices, setRespondAiChoices] = React.useState<AiChoice[]>([]);
  const [respondAiIndex, setRespondAiIndex] = React.useState(0);
  const [respondAiCommand, setRespondAiCommand] = React.useState('');
  const [respondIsSubmitting, setRespondIsSubmitting] = React.useState(false);

  // View campaigns state
  const [showCampaigns, setShowCampaigns] = React.useState(false);
  const [campaignIdFromHistory, setCampaignIdFromHistory] = React.useState<string | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = React.useState<string | null>(null);

  // View required actions state
  const [showRequiredActions, setShowRequiredActions] = React.useState(false);

  // Action section state
  const [actionEditingStep, setActionEditingStep] = React.useState<Step | undefined>();
  const [actionAutoGenerateAi, setActionAutoGenerateAi] = React.useState(false);
  const [actionSendMode, setActionSendMode] = React.useState<SendMode>(
    userTeam.scheduleIsWithinSchedule ? 'now' : 'scheduled',
  );

  // Reminder card state
  const [reminderDeletingIds, setReminderDeletingIds] = React.useState<Set<string>>(new Set());

  // Voice note upload state
  const [isUploadingRecording, setIsUploadingRecording] = useState(false);

  // Step editor state
  const [stepEditorChanges, setStepEditorChanges] = React.useState<StepChanges>({});
  const [stepEditorAiChoices, setStepEditorAiChoices] = React.useState<AiChoice[]>([]);
  const [stepEditorAiIndex, setStepEditorAiIndex] = React.useState(0);
  const [stepEditorAiCommand, setStepEditorAiCommand] = React.useState('');
  const [stepEditorThreadOptions, setStepEditorThreadOptions] = React.useState<
    { value: string; label: string; helperText?: string }[]
  >([]);

  // AI message section local state
  const [takeActionMessageInstructionsLocal, setTakeActionMessageInstructionsLocal] = React.useState(
    userTeam.takeActionMessageInstructions ?? '',
  );

  // Interact section state
  const [updatedStepId, setUpdatedStepId] = React.useState<string>();
  const [retryStates, setRetryStates] = React.useState<Record<string, 'idle' | 'pending' | 'success' | 'error'>>({});
  const [retryErrors, setRetryErrors] = React.useState<Record<string, string | null>>({});
  const [pendingRetryId, setPendingRetryId] = React.useState<string | null>(null);
  const [transcriptionRequestStates, setTranscriptionRequestStates] = React.useState<
    Record<string, 'idle' | 'pending' | 'success' | 'error'>
  >({});
  const [pendingTranscriptionId, setPendingTranscriptionId] = React.useState<string | null>(null);

  const authIsSuperadmin = propAuthIsSuperadmin ?? userTeam.isSuperAdmin;
  const [authData] = useAuthContext();
  const [q, setQuery, updateQuery] = useRouterQuery();
  const utils = api.useUtils();
  const router = useRouter();
  const contactSetStageMutation = useContactSetStageMutation();
  const orgSetStageMutation = useOrgSetStageMutation();
  const copyToMyTeamMutation = api.superAdmin.copyContactToMyTeam.useMutation({
    onSuccess: () => {
      void utils.contact.getConnected.invalidate();
    },
  });
  // Superadmin-only hard delete of the contact + related data. On success the
  // contact no longer exists, so we invalidate the lists it appeared in and
  // close the detail panel.
  const deleteContactMutation = api.superAdmin.deleteContact.useMutation({
    onSuccess: () => {
      void utils.contact.getConnected.invalidate();
      void utils.contact.getConnectedGroupedByOrg.invalidate();
      void utils.contact.byOrg2.invalidate();
      void utils.superAdmin.contact.invalidate();
      void utils.stage.withContacts.invalidate();
      void utils.stage.withOrgs.invalidate();
      void utils.history.contact.invalidate();
      void utils.sharing.paginated.invalidate();
    },
  });

  // ESC key handling for closing the detail panel
  useEscStack('ContactDetailPage', ESC_PRIORITY.MED, onClose ?? (() => {}));

  const contactId = data?.contactV2?.id ?? '';
  // Async-enrichment indicator for THIS contact (banner above the tabs).
  const { inFlightIds: enrichInFlightIds } = useEnrichmentStatus({
    userId: authUserId,
    teamId: authTeamId,
    contactIds: contactId ? [contactId] : [],
  });

  // All campaigns (cadences) this contact is in, so the header can show a switcher
  // when there are 2+ ACTIVE ones. Reuses the same query the campaigns overlay uses.
  const contactCampaignsQuery = api.cadence.forContact.useQuery(
    { contactId },
    { enabled: Boolean(contactId) },
  );
  const activeCampaigns = useMemo(
    () =>
      (contactCampaignsQuery.data ?? [])
        .filter((c) => BLOCKING_STATES.has(c.state))
        // Oldest → newest so pills keep a stable, sensible order across renders.
        .sort((a, b) => new Date(a.createdOn ?? 0).getTime() - new Date(b.createdOn ?? 0).getTime())
        .map((c) => ({ id: c.id, name: c.name, state: c.state })),
    [contactCampaignsQuery.data],
  );

  // Bundled contact-detail bootstrap: stages + reminderActions + globalReminders + sharing documents.
  // Add more sub-payloads on the server side as the page grows.
  const detailBootstrapQuery = api.contactDetailBootstrap.forContact.useQuery(
    { teamId: authTeamId, contactId },
    { enabled: Boolean(contactId) },
  );
  const showSignalsForSuperadmin = authIsSuperadmin || authData.impersonation != null;

  // Secondary contact-detail data bundled into one request (email credentials,
  // contact emails, staging candidates, active-cadence template, and — for
  // superadmins/impersonation — signals + ignored signal aspects). Resilient:
  // any sub-payload that fails server-side comes back null without failing the
  // rest. See contactDetailBootstrap.secondaryForContact.
  const detailSecondaryQuery = api.contactDetailBootstrap.secondaryForContact.useQuery(
    {
      contactId,
      teamId: authTeamId,
      includeSignals: showSignalsForSuperadmin,
    },
    { enabled: Boolean(contactId) },
  );
  // All image variants for the collapsed "Images" inspector on the data tab (source photo,
  // background-removed + trimmed cutouts, raw image-cache sizes). A power-user/debug affordance, so
  // it's gated to superadmin/impersonation like Delete Contact — regular users don't fetch it. Cheap
  // standalone query so it refetches independently after an Enhance regenerates the cutouts.
  const contactImagesQuery = api.contact.imageVariants.useQuery(
    { contactId },
    { enabled: Boolean(contactId) && showSignalsForSuperadmin },
  );

  const allEmailCredentials = detailSecondaryQuery.data?.emailCredentials ?? undefined;
  const contactEmails = detailSecondaryQuery.data?.emails ?? undefined;
  const stagingCandidatesData = detailSecondaryQuery.data?.stagingCandidates ?? undefined;
  const cadenceTemplateData = detailSecondaryQuery.data?.cadenceTemplate ?? undefined;

  // ContactDataSection mutations
  const verifyEmail = useVerifyContactEmailMutation();
  const markPhoneFirmNumber = useMarkPhoneFirmNumberMutation();
  const removeValue = useRemoveContactFieldValueMutation();
  const reassignPhoneToOrg = useReassignContactPhoneToOrgMutation();
  const updateContact = useUpdateContactMutation();
  const removePrimary = useRemoveContactFieldPrimaryMutation();
  const makePrimary = useMakeContactFieldPrimaryMutation();
  const disqualifiedMutation = useContactSetDisqualifiedMutation();
  const trackingMutation = useContactSetTrackingMutation();
  const researchMutation = useResearchFromLinkedInMutation();
  const enhanceMutation = useEnhanceContactMutation();
  const regenerateDossierMutation = useRegenerateDossierMutation();
  const deleteMeetingMutation = useDeleteMeetingBookedMutation();
  const overrideMutation = useContactSetMinAgeOverrideMutation();
  const resetGenderMutation = useContactResetGenderMutation();
  const deleteForeignMappingMutation = useDeleteForeignMappingMutation();
  const addForeignMappingMutation = useAddForeignMappingMutation();
  const verifyForeignMappingMutation = useVerifyForeignMappingMutation();

  const signals = (() => {
    if (!showSignalsForSuperadmin) return undefined;
    const rows = detailSecondaryQuery.data?.signals?.signals ?? [];
    const aspectCountByBucket = new Map<string, number>();
    for (const r of rows) {
      aspectCountByBucket.set(r.bucket_key, (aspectCountByBucket.get(r.bucket_key) ?? 0) + 1);
    }
    return rows.map((s) => ({
      bucket_key: s.bucket_key,
      bucket_name: s.bucket_name,
      bucket_aspect_count: aspectCountByBucket.get(s.bucket_key) ?? 1,
      aspect_key: s.aspect_key,
      aspect_name: s.aspect_name,
      aspect_description: s.aspect_description,
      aspect_psychological_strategic_angle: s.aspect_psychological_strategic_angle,
      value: s.value,
      reason: s.reason,
    }));
  })();
  const lastUnipileFetchAt = detailSecondaryQuery.data?.signals?.lastUnipileFetchAt ?? null;
  const isSignalsLoading = showSignalsForSuperadmin && detailSecondaryQuery.isLoading;
  const ignoredAspectKeys = detailSecondaryQuery.data?.ignoredSignalAspects
    ? new Set(detailSecondaryQuery.data.ignoredSignalAspects.aspectKeys)
    : undefined;

  // Campaign button mutations
  const pauseMutation = useCadencePauseAllMutation();
  const resumeMutation = useCadenceResumeAllMutation();
  const reattachMutation = useCadenceReattachMutation();
  const deleteCadenceMutation = useCadenceDeleteMutation();
  const changeOwnerMutation = useChangeCadenceOwnerMutation();
  const createCadenceMutation = useContactCreateCadenceMutation();
  // Adding a contact to a campaign switch from the enroll dropdown routes them
  // into a campaign by attribute (after research), instead of enrolling directly.
  const intakeIntoSwitchMutation = api.campaignSwitch.intakeIntoSwitch.useMutation({
    onSuccess: () => {
      void utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId });
    },
  });
  // Enrolling a contact into a sequence (the new graph-based journey model) from
  // the same dropdown. `assign` enrolls at the entry node and kicks the walk off
  // immediately; the enroll option id is namespaced `sequence:<publicId>`.
  const assignToSequenceMutation = api.sequence.assign.useMutation({
    onSuccess: () => {
      void utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId });
      void utils.sequence.forContact.invalidate({ teamId: userTeam.teamId, contactId });
    },
  });
  // Unenroll from a sequence (the "in sequence X" pill's trash). Clears the block so the enroll
  // dropdown returns — hence invalidating forContact on success.
  const removeFromSequenceMutation = api.sequence.remove.useMutation({
    onSuccess: () => {
      void utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId });
      void utils.sequence.forContact.invalidate({ teamId: userTeam.teamId, contactId });
    },
  });

  // Cadence step mutations (for CadenceStepsPure)
  const cadenceCreateStepMutation = useCadenceCreateStepMutation();
  const cadenceUpdateStepMutation = useCadenceUpdateStepMutation();
  const cadenceDeleteStepMutation = useCadenceDeleteStepMutation();
  const cadenceChangeStepStateMutation = useCadenceChangeStepStateMutation();
  const deleteFutureStepsMutation = useCadenceDeleteFutureStepsMutation();

  // ============================================================================
  // Action Section State, Queries, and Mutations
  // ============================================================================

  // Track if action step was open initially (for animation)
  const actionHadStepInitiallyRef = React.useRef<boolean | null>(null);
  if (actionHadStepInitiallyRef.current === null) {
    actionHadStepInitiallyRef.current = q.action != null;
  }

  // Action section queries
  const hasUnipile = userTeam.unipileAccountId != null;

  // Bundles the action section's pending-step + schedule-window queries into
  // one request. The detail only uses scheduleStatus from queueCounts (not the
  // counts), so the route stays light. See aiStep.actionSectionData.
  const actionSectionQuery = api.aiStep.actionSectionData.useQuery(
    { contactId, teamId: authTeamId, userId: authUserId },
    { enabled: Boolean(contactId) },
  );

  // Action section mutations
  const ackContentViewAndResponseMutation = useContactAckContentViewAndResponse();
  const sendEmailMutation = api.contact.sendEmail.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate({ contactId });
      void utils.history.contact.invalidate({ contactId });
    },
  });
  const aiStepBulkCreateMutation = useAiStepBulkCreateMutation();
  const { sendMessageViaMutation, sendConnectionViaMutation, scheduleQuickActionMutation } =
    useContactActionMutations(contactId);
  const deletePendingStepMutation = api.aiStep.delete.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate({ contactId });
      void utils.aiStep.fetchNext.invalidate();
    },
  });
  const createExperienceMutation = api.contact.createExperience.useMutation({
    onSuccess: (newExperience) => {
      utils.contactDetailBootstrap.forContact.setData({ teamId: authTeamId, contactId }, (prev) => {
        if (prev == null) return prev;
        return {
          ...prev,
          experienceAndEducation: {
            ...prev.experienceAndEducation,
            experiences: [newExperience, ...prev.experienceAndEducation.experiences],
          },
        };
      });
    },
  });
  const deleteExperienceMutation = api.contact.deleteExperience.useMutation({
    onSuccess: (_data, variables) => {
      utils.contactDetailBootstrap.forContact.setData({ teamId: authTeamId, contactId }, (prev) => {
        if (prev == null) return prev;
        return {
          ...prev,
          experienceAndEducation: {
            ...prev.experienceAndEducation,
            experiences: prev.experienceAndEducation.experiences.filter((e) => e.id !== variables.experienceId),
          },
        };
      });
    },
  });

  // Reminder card mutations
  const globalReminderCreateMutation = useGlobalReminderCreate();
  const globalReminderDeleteMutation = useGlobalReminderDelete();
  const globalReminderUpdateMutation = useGlobalReminderUpdate();
  const globalReminderChangeOwnerMutation = useGlobalReminderChangeOwner();

  // Voice note card mutations
  const sendAudioToContactMutation = api.contact.sendAudioToContact.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate({ contactId });
    },
  });
  const generateUploadUrlMutation = useGenerateUploadUrlMutation();
  const makePublicAudioFileUrlMutation = useMakePublicAudioFileUrlMutation();
  const generateVoiceNoteAudioMutation = useContactGenerateVoiceNoteAudioMutation();
  const approveVoiceNoteStepMutation = useContactApproveVoiceNoteStepMutation();
  const createVoiceNoteStepMutation = useContactCreateVoiceNoteStepMutation();
  const generateAttachmentUploadUrlMutation = api.messageAttachment.generateUploadUrl.useMutation();

  // File attachment staged for a LinkedIn message/inmail send (contact detail only).
  const [messageAttachment, setMessageAttachment] = useState<{ path: string; filename: string } | null>(null);
  const [isUploadingMessageAttachment, setIsUploadingMessageAttachment] = useState(false);

  const onSelectMessageAttachmentFile = React.useCallback(
    async (file: File) => {
      setIsUploadingMessageAttachment(true);
      try {
        await updateWithToast({
          loading: 'Uploading file...',
          success: 'File attached',
          mutationFnAsync: async () => {
            const { uploadUrl, path } = await generateAttachmentUploadUrlMutation.mutateAsync({
              teamId: authTeamId,
              filename: file.name,
              contentType: file.type || 'application/octet-stream',
              sizeBytes: file.size,
            });
            const res = await fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: { 'Content-Type': file.type || 'application/octet-stream' },
            });
            if (!res.ok) throw new Error('Upload failed');
            setMessageAttachment({ path, filename: file.name });
          },
        });
      } finally {
        setIsUploadingMessageAttachment(false);
      }
    },
    [authTeamId, generateAttachmentUploadUrlMutation],
  );

  const onRemoveMessageAttachment = React.useCallback(() => {
    setMessageAttachment(null);
  }, []);

  // Step editor mutations
  const emailInteractionsMutation = useEmailInteractionsSummary();
  const createReminderMutation = useCreateReminderMutation();
  const assistMutation = api.assistant.contact.useMutation({
    onSuccess: () => {
      void utils.aiStep.fetchNext.invalidate();
    },
  });
  const createSnippetMutation = api.snippet.createWithName.useMutation({
    onSuccess: () => {
      void utils.snippet.paginated.invalidate({ teamId: authTeamId });
    },
  });

  // Listen for extension messages
  usePostMessageListener('SEND_STEP_SUCCESSFULLY', () => {
    localStorage.removeItem('form');
    setActionEditingStep((prev) => {
      if (!prev) return;
      return { ...prev, subject: '', body2: '', weight: 0, delay: 0 };
    });
  });

  // Documents for TipTapInput (sourced from bootstrap query)
  const editorDocuments = useMemo(
    () => getEditorDocuments(detailBootstrapQuery.data?.sharing.docs, undefined),
    [detailBootstrapQuery.data?.sharing],
  );
  // Filtered documents for components expecting SvrDocument[] (excludes private placeholder docs)
  const svrDocuments = editorDocuments.filter((el) => !('isPrivate' in el)) as Document[];

  // Build template info for CadenceStepsPure (sourced from the combined query)
  const cadenceTemplateInfo = cadenceTemplateData
    ? {
        goal: cadenceTemplateData.goal ?? null,
        previewText: cadenceTemplateData.previewText ?? null,
        recentPosts: cadenceTemplateData.recentPosts,
      }
    : null;

  // Take Action message instructions (typed by the user in the action card)
  const updateUserTeamDataMutation = api.user.updateTeamData.useMutation({
    onSuccess: () => {
      void utils.user.me.invalidate();
      void utils.team.teamUser.invalidate({ teamId: authTeamId, userId: authUserId });
    },
  });

  const handleChangeAddToTakeActionOnlyNewPeople = React.useCallback(
    (next: boolean) => {
      if (next === userTeam.takeActionOnlyNewPeople) return;
      // Optimistically flip the chevron — feels instant. user.me gets
      // invalidated on success (reconciles with the server); rollback on
      // error.
      const prev = utils.user.me.getData();
      utils.user.me.setData(undefined, (old) => {
        if (!old || !('userTeam' in old) || !old.userTeam) return old;
        return { ...old, userTeam: { ...old.userTeam, takeActionOnlyNewPeople: next } };
      });
      updateUserTeamDataMutation.mutate(
        {
          userId: authUserId,
          teamId: authTeamId,
          data: { takeActionOnlyNewPeople: next },
        },
        {
          onError: () => {
            if (prev) utils.user.me.setData(undefined, prev);
          },
        },
      );
    },
    [authTeamId, authUserId, updateUserTeamDataMutation, userTeam.takeActionOnlyNewPeople, utils],
  );

  const saveTakeActionMessageInstructions = (value: string) =>
    updateWithToast({
      loading: 'Updating Take Action message instructions',
      success: 'Take Action message instructions updated',
      mutationFnAsync: () =>
        updateUserTeamDataMutation.mutateAsync({
          userId: authUserId,
          teamId: authTeamId,
          data: { takeActionMessageInstructions: value.length > 0 ? value : null },
        }),
      onAfterSuccess: () => {
        void utils.aiStep.superAdminBuildPreviewPrompt.invalidate();
      },
    });

  const debouncedSaveTakeActionMessageInstructions = useAsyncDebouncedCallback(saveTakeActionMessageInstructions, 600);
  // Smart-campaign AI message section was removed; keep aiMessageData unset.
  const aiMessageData: AIMessageSectionData | null = null;

  // ============================================================================
  // Step Editor Data and Callbacks
  // ============================================================================

  // Convert editor documents to EditorDocument format
  const stepEditorDocuments: EditorDocument[] = useMemo(
    () =>
      editorDocuments.map((doc) => ({
        id: doc.id,
        title: 'title' in doc ? doc.title : undefined,
        type: 'type' in doc ? (doc.type as string | undefined) : undefined,
        isPrivate: 'isPrivate' in doc ? doc.isPrivate : undefined,
        imageUrl: 'ogImageUrl' in doc ? documentThumbnailUrl(doc) : undefined,
      })),
    [editorDocuments],
  );

  // Email options
  const stepEditorToEmailOptions = useMemo(
    () =>
      (contactEmails?.emails ?? []).map((email) => ({
        value: email.data,
        label: email.primary ? `${email.data} (Primary)` : email.data,
      })),
    [contactEmails],
  );

  const stepEditorFromEmailOptions = useMemo(
    () =>
      (allEmailCredentials?.emailCredentials ?? []).map((credential) => {
        const lastUsed = data?.authedUser?.emailCredentialId === credential.id;
        const suffix = lastUsed ? ' (Last used)' : credential.primary ? ' (Primary)' : '';
        return {
          value: credential.email,
          label: credential.email + suffix,
        };
      }),
    [allEmailCredentials, data?.authedUser?.emailCredentialId],
  );

  // Step editor callbacks
  const handleStepEditorChange = React.useCallback((changes: StepChanges) => {
    setStepEditorChanges((current) => {
      const newObj = { ...current, ...changes };
      localStorage.setItem('savedStep', JSON.stringify(newObj));
      return newObj;
    });
  }, []);

  const handleStepEditorGenerateAi = React.useCallback(
    async (empty = false) => {
      const stepType = stepEditorChanges.type ?? actionEditingStep?.type;
      if (!stepType || !['email', 'inmail', 'connect', 'message'].includes(stepType)) return;

      try {
        const messageChoices = await assistMutation.mutateAsync({
          contactId,
          stepType: stepType as 'email' | 'inmail' | 'connect' | 'message',
          command: stepEditorAiCommand.length > 0 ? stepEditorAiCommand : undefined,
          partialBody: empty ? undefined : getTextFromHtml(stepEditorChanges.body2 ?? actionEditingStep?.body2 ?? ''),
        });

        const choices = messageChoices.map((choice) => ({
          subject: choice.subject,
          body: choice.body.replaceAll('\n\n', '<div><br/></div>'),
        }));

        // CadenceStepSectionPure applies the selected choice to its editor (and
        // propagates it back via onChange) when these props change — see its
        // aiChoices sync effect. Setting the body here too would double-apply.
        setStepEditorAiChoices(choices);
        setStepEditorAiIndex(0);
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        } else {
          toast.error('Failed to generate a message');
        }
      }
    },
    [
      contactId,
      stepEditorAiCommand,
      stepEditorChanges.type,
      stepEditorChanges.body2,
      actionEditingStep?.type,
      actionEditingStep?.body2,
      assistMutation,
    ],
  );

  const handleStepEditorLoadThreads = React.useCallback(async () => {
    if (stepEditorThreadOptions.length > 0) return;

    try {
      const emailInteractions = await emailInteractionsMutation.mutateAsync({
        teamId: authTeamId,
        userId: authUserId,
        contactId,
        limit: 15,
      });

      setStepEditorThreadOptions(
        emailInteractions.map((email) => ({
          label: getTextFromHtml(email.subject),
          value: getTextFromHtml(email.subject),
          helperText: new Date(email.date).toLocaleDateString(),
        })),
      );
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      } else {
        toast.error('Failed to get email interactions');
      }
    }
  }, [stepEditorThreadOptions.length, emailInteractionsMutation, authTeamId, authUserId, contactId]);

  const handleStepEditorCreateReminder = React.useCallback(
    async (name: string) => {
      await createReminderMutation.mutateAsync({ teamId: authTeamId, data: { name } });
    },
    [createReminderMutation, authTeamId],
  );

  const handleStepEditorToast = React.useCallback((message: string, type: 'error' | 'success') => {
    if (type === 'error') {
      toast.error(message, { style: { position: 'relative', bottom: '30px' } });
    } else {
      toast.success(message, { style: { position: 'relative', bottom: '30px' } });
    }
  }, []);

  const handleStepEditorSaveSnippet = React.useCallback(async () => {
    const body = stepEditorChanges.body2 ?? actionEditingStep?.body2;
    const subject = stepEditorChanges.subject ?? actionEditingStep?.subject;

    if (!body && !subject) return;

    // Generate a name from the body content or subject
    const bodyText = body ? getTextFromHtml(body).slice(0, 50).trim() : '';
    const name = bodyText || subject?.slice(0, 50).trim() || 'Saved template';

    try {
      await createSnippetMutation.mutateAsync({
        teamId: authTeamId,
        data: {
          value: body ?? subject ?? '',
          name,
          subject: subject ?? undefined,
        },
      });
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message, { style: { position: 'relative', bottom: '30px' } });
      } else {
        toast.error('Failed to save template', { style: { position: 'relative', bottom: '30px' } });
      }
    }
  }, [
    stepEditorChanges.body2,
    stepEditorChanges.subject,
    actionEditingStep?.body2,
    actionEditingStep?.subject,
    authTeamId,
    createSnippetMutation,
  ]);

  // Build step editor data for pure component
  // Hide the `call` step from the step-type changer + respond pickers unless the team has voice
  // calling enabled — mirrors the campaign/template picker gating. Without this, `call` leaks
  // into these dropdowns ungated (cadenceStepsList already contains callStep).
  const gatedCadenceStepsList = React.useMemo(
    () => (userTeam.voiceCallingEnabled ? cadenceStepsList : cadenceStepsList.filter((s) => s.value !== 'call')),
    [userTeam.voiceCallingEnabled],
  );

  const stepEditorData: StepEditorData | undefined = actionEditingStep
    ? {
        documents: stepEditorDocuments,
        fromEmailOptions: stepEditorFromEmailOptions,
        toEmailOptions: stepEditorToEmailOptions,
        threadOptions: stepEditorThreadOptions,
        reminderActions: detailBootstrapQuery.data?.reminderActions.reminderActions ?? [],
        stepsList: gatedCadenceStepsList,
        aiChoices: stepEditorAiChoices,
        aiIndex: stepEditorAiIndex,
        aiCommand: stepEditorAiCommand,
        isAiGenerating: assistMutation.isPending,
        isLoadingReminderActions: detailBootstrapQuery.isLoading,
        isCreatingReminderAction: createReminderMutation.isPending,
        isSavingSnippet: createSnippetMutation.isPending,
      }
    : undefined;

  // Build step editor callbacks for pure component
  const stepEditorCallbacks: StepEditorCallbacks | undefined = actionEditingStep
    ? {
        onGenerateAi: handleStepEditorGenerateAi,
        onAiCommandChange: setStepEditorAiCommand,
        onAiIndexChange: setStepEditorAiIndex,
        onLoadThreads: handleStepEditorLoadThreads,
        onCreateReminderAction: handleStepEditorCreateReminder,
        onSaveSnippet: handleStepEditorSaveSnippet,
        onToast: handleStepEditorToast,
        onStepChange: handleStepEditorChange,
      }
    : undefined;

  // ============================================================================
  // Action Section Helpers and Callbacks
  // ============================================================================

  // Get action step from localStorage and URL action
  const getActionStep = React.useCallback(
    (action: string | undefined): Step | undefined => {
      if (action == null) return undefined;
      if (!data?.contactV2) return undefined;

      const defaultForm = localStorage.getItem('savedStep');
      const formData = actionFormSchema.parse(defaultForm ? JSON.parse(defaultForm) : {});

      const defaultFormSubject = formData.subject ?? '';
      const defaultFormBody = formData.body2 ?? '';
      const actionAtVariables = {
        ...fakeAtVariables,
        ...svrExtractAtVariables(userTeam, data.contactV2, data.contactOrg),
      };
      const [body2] = convertTemplateToHtml(defaultFormBody, actionAtVariables, editorDocuments);
      const [subject] = convertTemplateToHtml(defaultFormSubject, actionAtVariables, editorDocuments);

      if (!isSeedableActionStepType(action)) return undefined;

      const step: Step = {
        id: '',
        type: action,
        body2,
        subject,
        from: null,
        to: null,
        cc: null,
        bcc: null,
        onlyOpenLink: false,
        useEmailSignature: false,
      };

      if (action === 'email') {
        const toEmail =
          data.contactV2.fields?.professionalEmail?.find((f: { primary: boolean }) => f.primary)?.data ?? null;
        step.to = toEmail;
        step.cc = formData.cc ?? null;
        step.bcc = formData.bcc ?? null;
      } else if (action === 'message' || action === 'connect') {
        step.subject = null;
      }

      return step;
    },
    [userTeam, data?.contactV2, data?.contactOrg, editorDocuments],
  );

  // Seed the editing step from the URL action (adjust-during-render instead of
  // an effect). Same trigger as the old effect — `q.action` present while no
  // step is being edited yet — but without the extra commit+render. Once seeded,
  // `actionEditingStep` is non-null so this stops firing, and user edits to it
  // survive because a stable `q.action` never re-triggers the seed.
  //
  // The decision lives in `computeActionEditorSeed` (pure + unit-tested): it
  // returns `undefined` — so we never `setState` — for values `getActionStep`
  // can't build, notably the interaction-action values the "Reply inline" flow
  // writes to `q.action` (`message_received`, …) or the pre-data-load window.
  // Seeding `undefined` here would leave `actionEditingStep` falsy and re-fire
  // this render-phase update every render → "Too many re-renders".
  const seededActionStep = computeActionEditorSeed({
    action: q.action,
    actionEditingStep,
    buildActionStep: getActionStep,
  });
  if (seededActionStep) {
    setActionEditingStep(seededActionStep);
  }

  // Cancel action editing
  const onCancelAction = React.useCallback(() => {
    setActionEditingStep(undefined);
    setActionAutoGenerateAi(false);
    localStorage.removeItem('form');
    updateQuery({ action: undefined });
  }, [updateQuery]);

  // Clear action fields (body/subject) while keeping the section open
  const clearActionFields = React.useCallback(() => {
    setActionEditingStep((prev) => {
      if (!prev) return prev;
      return { ...prev, subject: '', body2: '' };
    });
    setStepEditorChanges({});
    localStorage.removeItem('savedStep');
  }, []);

  const clearActionDraft = React.useCallback(() => {
    setStepEditorChanges({});
    localStorage.removeItem('savedStep');
  }, []);

  // Handle action change (selecting an action type)
  const handleActionChange = React.useCallback(
    (action: string | undefined) => {
      if (action) {
        const stepType = action as CadenceStepType;
        if (stepType === 'message' || stepType === 'connect' || stepType === 'email' || stepType === 'inmail') {
          setActionEditingStep((s) => {
            const initial = getActionStep(stepType);
            if (s == null) return initial;
            if (stepType === 'email') return { ...s, type: stepType };
            return { ...s, type: stepType, from: null, to: null, cc: null, bcc: null };
          });
        }
      } else {
        onCancelAction();
        return;
      }
      updateQuery({ action });
    },
    [getActionStep, onCancelAction, updateQuery],
  );

  // Add to take action
  const handleAddToTakeAction = React.useCallback(
    async (onlyNewPeople: boolean) => {
      if (!contactId) return;
      const toastId = toast.loading('Adding to Take Action...');
      try {
        const result = await aiStepBulkCreateMutation.mutateAsync({
          teamId: authTeamId,
          userId: authUserId,
          data: { contactIds: [contactId], type: 'message' },
          onlyNewPeople,
        });
        const skippedReasons: string[] = [];
        if (result.skippedDisqualified > 0) {
          skippedReasons.push(`${result.skippedDisqualified} disqualified`);
        }
        if (result.skippedAlreadyInQueue > 0) {
          skippedReasons.push(`${result.skippedAlreadyInQueue} already in queue`);
        }
        if (result.skippedContactIds.length > 0) {
          skippedReasons.push('previously added');
        }
        const skippedMessage = skippedReasons.length > 0 ? `. Skipped: ${skippedReasons.join(', ')}` : '';
        toast.success(`Added ${result.created} to Take Action${skippedMessage}`, { id: toastId });
      } catch {
        toast.error('Failed to add to Take Action', { id: toastId });
      }
    },
    [aiStepBulkCreateMutation, authTeamId, authUserId, contactId],
  );

  // Submit action step
  const handleSubmitActionStep = React.useCallback(
    async (step: CadenceAddStepV2Params, sendModeOverride?: SendMode) => {
      if (!contactId) return;
      const cadence = data?.activeCadence?.cadence;

      // Use override if provided, otherwise fall back to state
      const effectiveSendMode = sendModeOverride ?? actionSendMode;

      await ackContentViewAndResponseMutation.mutateAsync({ contactId });

      try {
        // Email via Gmail API
        if (step.type === 'email' && userTeam.mailType === 'gmail-api') {
          if (step.subject == null || step.body2 == null) {
            toast.error('Subject and body are required');
            return;
          }

          const emailData = {
            subject: step.subject,
            body: step.body2,
            from: step.from ? step.from : undefined,
            cc: step.cc ? step.cc : undefined,
            bcc: step.bcc ? step.bcc : undefined,
            useEmailSignature: step.useEmailSignature,
          };

          const { code } = await sendEmailMutation.mutateAsync({ contactId, data: emailData });
          switch (code) {
            case 'SENT':
              Toast.success({ title: 'Sent email', message: 'Yay! The email was sent successfully.', duration: 4000 });
              clearActionDraft();
              onCancelAction();
              return;
            case 'NEW_RESPONSE':
              Toast.info({
                title: 'New response detected!',
                message: 'The email was not sent because a new email response was detected.',
                ctaText: 'Check it out',
                onCtaClick: () => updateQuery({ detailType: 'interact' }),
              });
              return;
            case 'UN_ACKNOWLEDGED_RESPONSE':
              Toast.error({
                title: 'Unacknowledged response!',
                message: 'The email was not sent because there is an unacknowledged response.',
                ctaText: 'Check it out',
                onCtaClick: () => updateQuery({ detailType: 'interact' }),
              });
              return;
            default:
              Sentry.captureMessage('Unhandled response code from contact.sendEmail', { extra: { code } });
              toast.error('Failed to send email.');
              return;
          }
        }

        // LinkedIn message/inmail via Unipile
        else if (step.type === 'message' || step.type === 'inmail') {
          if (hasUnipile) {
            if (!step.body2 && !messageAttachment) {
              toast.error('Message content is required');
              return;
            }

            if (effectiveSendMode === 'scheduled') {
              await updateWithToast({
                loading: 'Scheduling message...',
                success: `Message scheduled for ${userTeam.scheduleIsWithinSchedule ? 'a few minutes from now' : (userTeam.scheduleNextWindowStartFormatted ?? 'later')}`,
                mutationFnAsync: () =>
                  scheduleQuickActionMutation.mutateAsync({
                    contactId,
                    teamId: authTeamId,
                    userId: authUserId,
                    body: step.body2,
                    subject: step.type === 'inmail' ? step.subject : undefined,
                    stepType: step.type === 'inmail' ? 'inmail' : 'message',
                    replaceExisting: true,
                  }),
                onAfterSuccess: () => {
                  void actionSectionQuery.refetch();
                  clearActionFields();
                },
              });
              return;
            } else {
              const messageResult = await sendMessageViaMutation
                .mutateAsync({
                  contactId,
                  teamId: authTeamId,
                  userId: authUserId,
                  text: step.body2 ?? '',
                  isInmail: step.type === 'inmail',
                  subject: step.type === 'inmail' ? step.subject : undefined,
                  attachment: messageAttachment ?? undefined,
                })
                .catch((e: unknown) => {
                  const errorMessage = isTRPCClientError(e)
                    ? e.message
                    : e instanceof Error
                      ? e.message
                      : 'Failed to send message';
                  toast.error(errorMessage, { duration: 5000 });
                  return null;
                });
              if (messageResult) {
                toast.success(step.type === 'inmail' ? 'InMail sent' : 'Message sent');
                setMessageAttachment(null);
                if (cadence) await deleteFutureStepsMutation.mutateAsync({ cadenceId: cadence.id });
                clearActionDraft();
                onCancelAction();
              }
              return;
            }
          }

          // No Unipile connection — a LinkedIn message/InMail can't be sent. Block
          // with a toast and keep the composer open (draft preserved).
          const message = 'Unipile is not connected. Please connect your LinkedIn account first.';
          toast.error(message, { duration: 5000 });
          return;
        }

        // Connection request via Unipile
        else if (step.type === 'connect') {
          if (hasUnipile) {
            if (actionSendMode === 'scheduled') {
              await updateWithToast({
                loading: 'Scheduling connection request...',
                success: `Connection request scheduled for ${userTeam.scheduleIsWithinSchedule ? 'a few minutes from now' : (userTeam.scheduleNextWindowStartFormatted ?? 'later')}`,
                mutationFnAsync: () =>
                  scheduleQuickActionMutation.mutateAsync({
                    contactId,
                    teamId: authTeamId,
                    userId: authUserId,
                    body: step.body2 || undefined,
                    stepType: 'connect',
                    replaceExisting: true,
                  }),
                onAfterSuccess: () => {
                  void actionSectionQuery.refetch();
                  clearActionFields();
                },
              });
              return;
            }

            const result = await sendConnectionViaMutation
              .mutateAsync({
                contactId,
                teamId: authTeamId,
                userId: authUserId,
                message: step.body2 || undefined,
              })
              .catch((e: unknown) => {
                const errorMessage = isTRPCClientError(e)
                  ? e.message
                  : e instanceof Error
                    ? e.message
                    : 'Failed to send connection request';
                toast.error(errorMessage, { duration: 5000 });
                return null;
              });

            if (result) {
              toast.success('Connection request sent');
              clearActionFields();
            }
            return;
          }

          // No Unipile connection — the connection request can't be sent. Block
          // with a toast and keep the composer open (draft preserved).
          const message = 'Unipile is not connected. Please connect your LinkedIn account first.';
          toast.error(message, { duration: 5000 });
          return;
        }

        // Email without Gmail — no direct send channel here (the gmail-api branch
        // returned above). Block with a toast and keep the composer open.
        if (step.type === 'email') {
          const noChannel = 'No email channel connected. Connect Gmail to send email.';
          toast.error(noChannel, { duration: 5000 });
          return;
        }
      } catch (e: unknown) {
        if (e instanceof Error) toast.error(e.message || 'Failed to submit.', { duration: 5000 });
      }
    },
    [
      contactId,
      data?.activeCadence?.cadence,
      ackContentViewAndResponseMutation,
      userTeam,
      sendEmailMutation,
      updateQuery,
      hasUnipile,
      actionSendMode,
      scheduleQuickActionMutation,
      actionSectionQuery,
      onCancelAction,
      clearActionFields,
      clearActionDraft,
      sendMessageViaMutation,
      sendConnectionViaMutation,
      deleteFutureStepsMutation,
      authTeamId,
      authUserId,
      messageAttachment,
    ],
  );

  // Get initial step for action section
  const getInitialActionStep = React.useCallback(
    (type: StepType): ActionStep => {
      const svrStep = getActionStep(type);
      return {
        id: svrStep?.id ?? '',
        type,
        subject: svrStep?.subject ?? null,
        body2: svrStep?.body2 ?? null,
        from: svrStep?.from ?? null,
        to: svrStep?.to ?? null,
        cc: svrStep?.cc ?? null,
        bcc: svrStep?.bcc ?? null,
        useEmailSignature: svrStep?.useEmailSignature,
      };
    },
    [getActionStep],
  );

  // ContactInteractSection hooks

  // Mark meeting booked mutation
  const markMeetingBookedMutation = useMarkInteractionAsMeetingBookedMutation();

  // Smart campaign send connect audio mutation

  // Extract data variables before early return (nullable for hooks that need them)
  const contact = data?.contactV2 ?? null;
  const contactTeam = data?.team ?? null;
  const contactAuthedUser = data?.authedUser ?? null;
  const contactOrg = data?.contactOrg ?? null;
  const activeCadence = data?.activeCadence ?? null;

  // Touch-campaign graph for the Interact tab. When the contact's active cadence
  // was spawned from a touch campaign, the vertical step timeline is replaced by
  // the same branching node-canvas the template page renders (with a Minimal /
  // Expanded toggle). `idV2` returns flat steps; we build the graph client-side
  // exactly like `/templates/[templateId]` does. Edits persist to the template.
  const activeCadenceTemplateId = activeCadence?.cadence.templateId ?? null;
  const cadenceTemplateQuery = api.cadenceTemplate.idV2.useQuery(
    { cadenceTemplateId: activeCadenceTemplateId ?? '' },
    { enabled: activeCadenceTemplateId != null },
  );
  const touchCampaignTemplate = useMemo(() => {
    const template = cadenceTemplateQuery.data?.cadenceTemplate;
    if (template == null || template.touchCampaignStepType == null) return null;
    return {
      id: template.id,
      teamId: template.teamId,
      stepsGraph: cadenceStepBuildGraph(template.steps ?? []) as unknown as {
        nodes: LocalGraphNodes;
        startId: string;
      } | null,
      touchCampaignStepType: template.touchCampaignStepType,
    };
  }, [cadenceTemplateQuery.data]);
  // Shared editor wiring (documents / reminders / sift / @-variables) — the same
  // hook that feeds the template page and the campaign-switch editor.
  const touchEditorContext = useTouchEditorContext(userTeam, {
    contact: contact ?? undefined,
    contactOrg: contactOrg ?? undefined,
  });

  // The contact's OWN step graph (each node carries its per-contact state). Drives
  // the vertical timeline and — on touch campaigns — the per-step status overlay on
  // the node graph. Built once here so both consumers share it.
  const cadenceStepsGraph = useMemo<CadenceGraph | null>(
    () => (activeCadence ? (cadenceStepBuildGraph(activeCadence.cadenceSteps ?? []) as unknown as CadenceGraph) : null),
    [activeCadence],
  );

  // Per-contact status keyed by the INSTANCE graph node id, for overlaying done /
  // action-needed / errored / skipped onto the contact's own touch graph. The
  // instance graph's nodes ARE cadence steps (node.id === the step id the canvas
  // renders + edits), so we key on node.id directly.
  const contactStepStatuses = useMemo<StatusByTemplateStepId | null>(() => {
    if (cadenceStepsGraph == null) return null;
    const map: StatusByTemplateStepId = {};
    for (const node of Object.values(cadenceStepsGraph.nodes)) {
      map[node.id] = {
        cadenceStepId: node.id,
        state: node.state,
        completedOn: node.completedOn ?? null,
        errorMessage: node.errorMessage ?? null,
        skipReason: node.skipReason ?? null,
      };
    }
    return map;
  }, [cadenceStepsGraph]);

  // Same branching canvas the template page renders, but driven by THIS CONTACT'S
  // cadence INSTANCE — their own steps and personalized (evaluated) content —
  // rather than the shared template. Null (→ template-graph fallback) when the
  // instance graph can't be built, e.g. an active cadence with no steps yet.
  const touchCampaignInstance = useMemo(() => {
    const template = cadenceTemplateQuery.data?.cadenceTemplate;
    if (template == null || template.touchCampaignStepType == null || cadenceStepsGraph == null) return null;
    return {
      id: template.id,
      teamId: template.teamId,
      stepsGraph: cadenceStepsGraph as unknown as { nodes: LocalGraphNodes; startId: string } | null,
      touchCampaignStepType: template.touchCampaignStepType,
    };
  }, [cadenceTemplateQuery.data, cadenceStepsGraph]);

  // Retry attachment mutation
  const retryMutation = api.history.retryAttachment.useMutation({
    onSuccess: (_, variables) => {
      setRetryStates((prev) => ({ ...prev, [variables.interactionId]: 'pending' }));
      setPendingRetryId(variables.interactionId);
    },
    onError: (error, variables) => {
      setRetryStates((prev) => ({ ...prev, [variables.interactionId]: 'error' }));
      setRetryErrors((prev) => ({ ...prev, [variables.interactionId]: error.message }));
    },
  });

  // Poll for attachment download completion after retry initiated (more reliable than WebSocket)
  React.useEffect(() => {
    if (!pendingRetryId || !contactId) return;

    let cancelled = false;
    const interactionId = pendingRetryId;

    const pollInterval = setInterval(() => {
      if (cancelled) return;

      void utils.history.contact.fetch({ contactId, filter: { invert: true } }).then((freshData) => {
        if (cancelled || !freshData?.data) return;

        const interaction = freshData.data.find((h) => h.interaction.id === interactionId);
        if (!interaction) return;

        // Check if attachment has been downloaded (has data_url)
        if (interaction.interaction.data_url) {
          setRetryStates((prev) => ({ ...prev, [interactionId]: 'success' }));
          setPendingRetryId(null);

          // For audio/video, transcription is auto-queued. Start transcription polling.
          if (
            (interaction.interaction.data_url_type === 'audio' || interaction.interaction.data_url_type === 'video') &&
            (interaction.interaction.transcription_status === 'pending' ||
              interaction.interaction.transcription_status === 'processing')
          ) {
            setTranscriptionRequestStates((prev) => ({ ...prev, [interactionId]: 'pending' }));
            setPendingTranscriptionId(interactionId);
          }
        }
      });
    }, 2000);

    const timeout = setTimeout(() => {
      if (cancelled) return;
      setRetryStates((prev) => ({ ...prev, [interactionId]: 'error' }));
      setRetryErrors((prev) => ({ ...prev, [interactionId]: 'Timeout waiting for download' }));
      setPendingRetryId(null);
    }, 120000); // 2 minute timeout

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [pendingRetryId, contactId, utils.history.contact]);

  const handleRetryAttachment = (interactionId: string) => {
    setRetryStates((prev) => ({ ...prev, [interactionId]: 'idle' }));
    setRetryErrors((prev) => ({ ...prev, [interactionId]: null }));
    retryMutation.mutate({ interactionId });
  };

  // Transcription request mutation
  const requestTranscriptionMutation = api.history.requestTranscription.useMutation({
    onMutate: (variables) => {
      setTranscriptionRequestStates((prev) => ({ ...prev, [variables.interactionId]: 'pending' }));
    },
    onSuccess: (_, variables) => {
      // Task was queued - keep 'pending' state and start polling for completion
      setPendingTranscriptionId(variables.interactionId);
    },
    onError: (_, variables) => {
      setTranscriptionRequestStates((prev) => ({ ...prev, [variables.interactionId]: 'error' }));
    },
  });

  // Poll for transcription completion (more reliable than WebSocket for short-lived tasks)
  React.useEffect(() => {
    if (!pendingTranscriptionId || !contactId) return;

    let cancelled = false;

    const pollInterval = setInterval(() => {
      if (cancelled) return;

      // Fetch fresh data (waits for refetch to complete, unlike invalidate + getData)
      void utils.history.contact.fetch({ contactId, filter: { invert: true } }).then((freshData) => {
        if (cancelled || !freshData?.data) return;

        const interaction = freshData.data.find((h) => h.interaction.id === pendingTranscriptionId);
        if (!interaction) return;

        const status = interaction.interaction.transcription_status;
        if (status === 'completed' || status === 'failed') {
          setTranscriptionRequestStates((prev) => ({
            ...prev,
            [pendingTranscriptionId]: status === 'completed' ? 'success' : 'error',
          }));
          setPendingTranscriptionId(null);
        }
      });
    }, 2000); // Poll every 2 seconds

    // Timeout after 60 seconds
    const timeout = setTimeout(() => {
      if (cancelled) return;
      setTranscriptionRequestStates((prev) => ({ ...prev, [pendingTranscriptionId]: 'error' }));
      setPendingTranscriptionId(null);
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [pendingTranscriptionId, contactId, utils.history.contact]);

  const handleRequestTranscription = (interactionId: string) => {
    requestTranscriptionMutation.mutate({ interactionId });
  };

  // Record manual call mutation (for ActionCardPure tel links)
  const recordManualCallMutation = api.history.recordManualCall.useMutation({
    onSuccess: () => {
      void utils.history.contact.invalidate({ contactId });
    },
  });
  const handleRecordManualCall = () => {
    if (!contactId) return;
    recordManualCallMutation.mutate({ contactId });
  };

  // "Mark as done" on a cadence call step: the rep called from their own phone (or dialed then
  // skipped the disposition). One mutation logs the disposition — deduped server-side by
  // cadenceStepId so the step ends with exactly one call record — and finishes the step.
  const completeCallStepMutation = api.voiceCall.completeCallStep.useMutation();
  const handleMarkCallStepDone = async ({
    cadenceStepId,
    outcome,
    notes,
    callbackAt,
  }: {
    cadenceStepId: string;
    outcome: string;
    notes?: string;
    callbackAt?: number;
  }) => {
    if (!contactId || !isCompleteCallOutcome(outcome)) return;
    await updateWithToast({
      loading: 'Logging call…',
      // A "wrong number" outcome also removes the called number from the contact server-side.
      success: outcome === 'wrong_number' ? WRONG_NUMBER_MARKED_TOAST : 'Call step completed',
      mutationFnAsync: () =>
        completeCallStepMutation.mutateAsync({
          teamId: authTeamId,
          contactId,
          cadenceStepId,
          outcome,
          notes,
          callbackAt,
        }),
    });
    // Mirror the dialer's onCallLogged refresh: the step is finished server-side (reminder
    // deleted, cadence advanced) and the call is now on the history thread.
    void utils.contactDetailBootstrap.forContact.invalidate({ teamId: authTeamId, contactId });
    void utils.contact.byId.invalidate({ contactId });
    void utils.history.contact.invalidate({ contactId });
    // A non-connecting outcome advances the contact's from-number server-side, so the dialer's
    // cached "Calling from" would otherwise show the number this just rotated away from.
    void utils.voiceCall.getContactDialInfo.invalidate({ teamId: authTeamId, contactId });
    void actionSectionQuery.refetch();
  };

  // Unipile connect handler (for UnipileConnectBannerPure)
  const unipileAuthMutation = useUnipileAuthMutation();
  const handleUnipileConnect = async () => {
    try {
      const result = await unipileAuthMutation.mutateAsync();

      if (result.status === 'healthy') {
        toast.success('LinkedIn already connected!');
        return;
      }

      if (result.status === 'error') {
        toast.error(result.message);
        return;
      }

      if (result.url) {
        const popup = window.open(result.url, '_blank', 'height=700,width=600');

        const pollInterval = setInterval(() => {
          void (async () => {
            try {
              await utils.user.me.invalidate();
              const userData = utils.user.me.getData();

              if (userData?.userTeam?.unipileAccountId) {
                clearInterval(pollInterval);
                toast.success('LinkedIn connected successfully!');
                popup?.close();
              }
            } catch {
              // Ignore polling errors
            }
          })();
        }, 2000);

        setTimeout(() => {
          clearInterval(pollInterval);
        }, 180000);
      }
    } catch {
      // Error toast is shown via onError in useUnipileAuthMutation hook
    }
  };

  // Delete pending step handler (for PendingStepBannerPure)
  const handleDeletePendingStep = async () => {
    const pendingStep = actionSectionQuery.data?.pendingStep;
    if (!pendingStep) return;

    await updateWithToast({
      loading: 'Deleting scheduled message...',
      success: 'Scheduled message deleted',
      mutationFnAsync: () =>
        deletePendingStepMutation.mutateAsync({
          stepId: pendingStep.id,
          teamId: authTeamId,
          userId: authUserId,
        }),
      onAfterSuccess: () => {
        void actionSectionQuery.refetch();
      },
    });
  };

  // History query for interact section
  const historyQuery = api.history.contact.useQuery(
    { contactId, filter: { invert: true } },
    { enabled: Boolean(contactId) },
  );

  const isImpersonating = authData.impersonation != null;

  const onDebouncedNotesChange = async (notes: string) => {
    if (!contactId) return;
    try {
      await updateContact.mutateAsync({ contactId, data: { source: 'crm', notes } });
    } catch (error) {
      toast.error((error as Error).message);
    }
  };

  // Campaign button data
  const campaignTemplates: TemplateOption[] = (detailBootstrapQuery.data?.cadenceTemplates.data ?? []).map((t) => ({
    id: t.id,
    name: t.name,
    ownerIcon: (
      <Avatar
        alt={t.ownerName || '?'}
        srcs={[t.ownerProfilePicture]}
        shape="circle"
        coloredBackground
        className="size-full shrink-0"
      />
    ),
  }));

  const campaignOwners: OwnerOption[] = (detailBootstrapQuery.data?.teamUsers.teamUsers ?? []).map((u) => ({
    userId: u.userId,
    name: u.userTeamName ?? u.userEmail,
    profilePicture: u.profilePicture,
  }));

  // Campaign switches surface in the same enroll dropdown (gated on the flag).
  // Their option id is namespaced `switch:<publicId>` so onCampaignEnroll can
  // route them to intakeIntoSwitch instead of a direct cadence enroll.
  const switchesQuery = api.campaignSwitch.list.useQuery(
    { teamId: userTeam.teamId },
    { enabled: userTeam.campaignSwitchEnabled === true },
  );
  const switchEnrollOptions: TemplateOption[] = (switchesQuery.data?.switches ?? []).map((s) => ({
    id: `switch:${s.public_id}`,
    name: `${s.name} (switch)`,
  }));

  // Sequences (the new graph-based journey model) surface in the same enroll
  // dropdown, gated on the sequenceEnabled flag. Their option id is namespaced
  // `sequence:<publicId>` so onCampaignEnroll routes them to sequence.assign
  // instead of a direct cadence enroll.
  const sequencesQuery = api.sequence.list.useQuery(
    { teamId: userTeam.teamId },
    { enabled: userTeam.sequenceEnabled === true },
  );
  const sequenceEnrollOptions: TemplateOption[] = (sequencesQuery.data?.sequences ?? []).map((s) => ({
    id: `sequence:${s.id}`,
    name: `${s.name} (sequence)`,
  }));
  const enrollOptions: TemplateOption[] = [...campaignTemplates, ...switchEnrollOptions, ...sequenceEnrollOptions];

  // Live sequence enrollments for THIS contact — drives the "in sequence X" pill that BLOCKS the
  // enroll dropdown (mirrors an active cadence). The rows already match ActiveSequenceEnrollment
  // structurally, so they pass straight through. `assign` / `remove` below invalidate this query so the
  // pill appears / clears immediately.
  const contactSequencesQuery = api.sequence.forContact.useQuery(
    { teamId: userTeam.teamId, contactId },
    { enabled: userTeam.sequenceEnabled === true && Boolean(contactId) },
  );
  const activeSequences = contactSequencesQuery.data?.sequences ?? [];

  // Build at variables for action section (must be before early return).
  // `svrExtractAtVariables` handles `contactOrg = null | undefined` and falls
  // back to the contact's company/companyFull field, so we only gate on
  // `contact`. Previously gated on `contact && contactOrg`, which caused the
  // step preview to render `{Company}` for any contact without an org even
  // though the action editor (which uses a different map below) resolved it
  // correctly.
  const atVariables = React.useMemo(
    () => ({
      ...fakeAtVariables,
      ...(contact ? svrExtractAtVariables(userTeam, contact, contactOrg) : {}),
    }),
    [contact, contactOrg, userTeam],
  );

  // TipTap-shaped `@`-variables / `/`-documents for the locked per-contact sequence graph's step editor —
  // this contact's real values, so an override previews/resolves for THIS person (same source the sequence
  // builder + touch-graph editors use).
  const sequenceEditorVariables = React.useMemo(() => convertAtVariablesToTipTap(atVariables), [atVariables]);
  const sequenceEditorDocuments = React.useMemo(
    () => convertDocumentsToTipTap(touchEditorContext.documents ?? []),
    [touchEditorContext.documents],
  );

  // Build reminder card data for pure component (must be before early return)
  const cadenceReminders = React.useMemo(
    () =>
      detailBootstrapQuery.data?.globalReminders.globalReminders?.filter(
        (
          g,
        ): g is typeof g & {
          globalReminder: typeof g.globalReminder & {
            cadenceStepType: NonNullable<typeof g.globalReminder.cadenceStepType>;
          };
        } => g.globalReminder.cadenceStepType != null,
      ) ?? [],
    [detailBootstrapQuery.data?.globalReminders.globalReminders],
  );
  const nonCadenceReminders = React.useMemo(
    () =>
      detailBootstrapQuery.data?.globalReminders.globalReminders?.filter(
        (g) => g.globalReminder.cadenceStepType == null,
      ) ?? [],
    [detailBootstrapQuery.data?.globalReminders.globalReminders],
  );

  // Clear reminderDeletingIds when items are actually removed from the data (must be before early return)
  React.useEffect(() => {
    const reminders = detailBootstrapQuery.data?.globalReminders.globalReminders;
    if (!reminders) return;
    // Prune ids whose reminder no longer exists. Reading the current set via the
    // functional updater (`prev`) — instead of closing over `reminderDeletingIds`
    // — keeps the deps exhaustive while still reacting to the reminders list.
    const existingIds = new Set(reminders.map((r) => r.globalReminder.id));
    setReminderDeletingIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((id) => {
        if (existingIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [detailBootstrapQuery.data?.globalReminders.globalReminders]);

  const reminderCardData: ReminderCardData = {
    currentUserId: authUserId,
    cadenceReminders: cadenceReminders.map((cr) => ({
      globalReminder: {
        id: cr.globalReminder.id,
        teamId: authTeamId,
        userId: cr.globalReminder.userId,
        contactId: cr.globalReminder.contactId,
        planNotificationAt: cr.globalReminder.planRunAt ? new Date(cr.globalReminder.planRunAt) : null,
        planRunAt: cr.globalReminder.planRunAt ? new Date(cr.globalReminder.planRunAt) : null,
        name: cr.globalReminder.body,
        body: cr.globalReminder.body,
        completed: false,
        completedAt: null,
        createdAt: cr.globalReminder.createdAt,
        cadenceStep: {
          stepId: cr.globalReminder.cadenceStepId ?? '',
          cadenceId: '',
          cadenceName: null,
          type: cr.globalReminder.cadenceStepType ?? 'Unknown',
        },
      },
    })),
    nonCadenceReminders: nonCadenceReminders.map((nr) => ({
      globalReminder: {
        id: nr.globalReminder.id,
        teamId: authTeamId,
        userId: nr.globalReminder.userId,
        contactId: nr.globalReminder.contactId,
        planNotificationAt: nr.globalReminder.planRunAt ? new Date(nr.globalReminder.planRunAt) : null,
        planRunAt: nr.globalReminder.planRunAt ? new Date(nr.globalReminder.planRunAt) : null,
        name: nr.globalReminder.body,
        body: nr.globalReminder.body,
        kind: nr.globalReminder.kind,
        completed: false,
        completedAt: null,
        createdAt: nr.globalReminder.createdAt,
        cadenceStep: null,
      },
      globalReminderContactUser: nr.globalReminderContactUser,
    })),
    defaultReminderAction: userTeam.defaultReminderId
      ? (detailBootstrapQuery.data?.reminderActions.reminderActions?.find(
          (ra) => ra.id === userTeam.defaultReminderId,
        ) ?? undefined)
      : undefined,
    reminderOptions: (detailBootstrapQuery.data?.reminderActions.reminderActions ?? []).map((ra) => ({
      value: ra.name,
      label: ra.name,
    })),
    teamUsers: (detailBootstrapQuery.data?.teamUsers.teamUsers ?? []).map((u) => ({
      userId: u.userId,
      profilePicture: u.profilePicture ?? null,
      userTeamName: u.userTeamName ?? null,
      userEmail: u.userEmail,
    })),
    isLoading: detailBootstrapQuery.isFetching && !detailBootstrapQuery.data?.globalReminders,
    deletingIds: reminderDeletingIds,
    createPending: globalReminderCreateMutation.isPending,
    updatePending: globalReminderUpdateMutation.isPending,
    changeOwnerPending: globalReminderChangeOwnerMutation.isPending,
    creatingReminderAction: createReminderMutation.isPending,
  };

  // Reminder card callbacks (defined before useMemo that references them)
  const handleCreateReminder = (planRunAt: number, body?: string) =>
    updateWithToast({
      loading: 'Creating...',
      success: 'Reminder created',
      mutationFnAsync: () =>
        globalReminderCreateMutation.mutateAsync({
          teamId: authTeamId,
          createParams: { body, planRunAt, contactId },
        }),
    });

  const handleUpdateReminder = (globalReminderId: string, planRunAt: number, body?: string) =>
    updateWithToast({
      loading: 'Updating...',
      success: 'Reminder updated',
      mutationFnAsync: () =>
        globalReminderUpdateMutation.mutateAsync({
          teamId: authTeamId,
          globalReminderId,
          data: { body, planRunAt },
        }),
    });

  const handleDeleteReminder = async (id: string) => {
    setReminderDeletingIds((prev) => new Set(prev).add(id));
    await updateWithToast({
      loading: 'Deleting...',
      success: 'Reminder deleted',
      mutationFnAsync: () => globalReminderDeleteMutation.mutateAsync({ globalReminderId: id }),
      onError: () => {
        setReminderDeletingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      },
    });
  };

  const handleChangeReminderOwner = (globalReminderId: string, newUserId: string) =>
    updateWithToast({
      loading: 'Changing owner...',
      success: 'Owner changed',
      mutationFnAsync: () =>
        globalReminderChangeOwnerMutation.mutateAsync({
          teamId: authTeamId,
          globalReminderId,
          newUserId,
        }),
    });

  const reminderCardCallbacks: ReminderCardCallbacks = {
    onCreateReminder: handleCreateReminder,
    onUpdateReminder: handleUpdateReminder,
    onDeleteReminder: handleDeleteReminder,
    onChangeOwner: handleChangeReminderOwner,
    onNavigateToInteract: () => {
      updateQuery({ detailType: 'interact' });
    },
    onCreateReminderAction: handleStepEditorCreateReminder,
  };

  // Build voice note card data for pure component
  const voiceNoteCardData: VoiceNoteCardData = {
    audioDropFiles: (detailBootstrapQuery.data?.audioDropFiles ?? []).map((f) => ({
      id: f.id,
      name: f.title,
      url: f.publicUrl,
    })),
    audioDropFilesIsLoading: detailBootstrapQuery.isLoading,
  };

  // Voice note card callbacks
  const voiceNoteCardCallbacks: VoiceNoteCardCallbacks = {
    onSendAudio: async (fileId: string) => {
      if (!contact?.id) return;
      await updateWithToast({
        loading: 'Sending...',
        success: 'Voice note sent',
        mutationFnAsync: () =>
          sendAudioToContactMutation.mutateAsync({
            userId: authUserId,
            teamId: authTeamId,
            fileId,
            contactId: contact.id,
          }),
      });
    },
    onGenerateVoiceNote: async (text: string, voiceDropId?: string) => {
      if (!text.trim() || !contact) return undefined;
      try {
        const result = await updateWithToastPromise({
          loading: 'Generating voice note...',
          success: 'Voice note generated!',
          mutationFnAsync: () =>
            generateVoiceNoteAudioMutation.mutateAsync({
              userId: authUserId,
              teamId: authTeamId,
              contactId: contact.id,
              text,
              voiceDropId,
            }),
        });
        if (result) {
          void utils.user.getAudioDropFiles.invalidate();
          return {
            stepId: result.stepId,
            audioUrl: result.audioUrl,
            audioId: result.audioId,
          };
        }
      } catch {
        // Error toast already shown by updateWithToastPromise
      }
      return undefined;
    },
    onApproveVoiceNote: (stepId: string) =>
      updateWithToast({
        loading: 'Scheduling voice note...',
        success: 'Voice note scheduled!',
        mutationFnAsync: () =>
          approveVoiceNoteStepMutation.mutateAsync({
            userId: authUserId,
            teamId: authTeamId,
            stepId,
          }),
      }),
    onGenerateAiDraft: async (command: string): Promise<string> => {
      if (!contact) return '';
      const res = await assistMutation.mutateAsync({
        contactId: contact.id,
        stepType: 'message',
        command,
      });
      return res[0]?.body ?? '';
    },
    onRecordingComplete: (audioBlob: Blob, _durationMs: number) => {
      if (!contact) return;
      void updateWithToast({
        loading: 'Uploading and scheduling voice note...',
        success: 'Voice note scheduled!',
        mutationFnAsync: async () => {
          setIsUploadingRecording(true);
          try {
            const { uploadUrl, path } = await generateUploadUrlMutation.mutateAsync({ teamId: authTeamId });
            const uploadResponse = await fetch(uploadUrl, {
              method: 'PUT',
              body: audioBlob,
              headers: { 'Content-Type': audioBlob.type || 'audio/webm' },
            });
            if (!uploadResponse.ok) throw new Error('Upload failed');
            const { id } = await makePublicAudioFileUrlMutation.mutateAsync({ teamId: authTeamId, filePath: path });

            // Create and approve the voice note step
            await createVoiceNoteStepMutation.mutateAsync({
              userId: authUserId,
              teamId: authTeamId,
              contactId: contact.id,
              audioFileId: id,
            });
          } finally {
            setIsUploadingRecording(false);
          }
        },
      });
    },
    isUploadingRecording,
    onRecordTrigger: () => {},
  };

  // Convert respondStep to StepData with template variables (must be before early return)
  const respondStepData: StepData | null = useMemo(() => {
    if (!respondStep) return null;
    const [body2Html] =
      respondStep.body2 == null ? [null] : convertTemplateToHtml(respondStep.body2, atVariables, editorDocuments);
    const [subjectHtml] =
      respondStep.subject == null ? [null] : convertTemplateToHtml(respondStep.subject, atVariables, editorDocuments);
    return {
      id: respondStep.id,
      type: respondStep.type,
      subject: subjectHtml,
      body2: body2Html,
      from: respondStep.from,
      to: respondStep.to,
      cc: respondStep.cc,
      bcc: respondStep.bcc,
      onlyOpenLink: respondStep.onlyOpenLink,
      useEmailSignature: respondStep.useEmailSignature,
    };
  }, [respondStep, atVariables, editorDocuments]);

  // Super-admin AI preview hooks must run unconditionally — before the early
  // returns below — to satisfy the rules of hooks. They are gated via `enabled`
  // and tolerate a still-loading (null) `contact`.
  const superAdminPromptShown = userTeam.isSuperAdmin || isImpersonating;
  const superAdminAiPreviewMutation = api.aiStep.superAdminGeneratePreview.useMutation();
  const superAdminPromptQuery = api.aiStep.superAdminBuildPreviewPrompt.useQuery(
    { teamId: authTeamId, userId: authUserId, contactId: contact?.id ?? '' },
    { enabled: superAdminPromptShown && contact != null },
  );

  // Loading vs not-found are now their own dedicated components rendered
  // by the parent (`ContactDetailPage`), so by the time we get here we
  // can assume `data` is non-null. These guards stay as defensive paths
  // in case a different host calls this component directly.
  if (isLoading) {
    return <ContactDetailPageSkeleton />;
  }
  if (data == null) {
    return <ContactDetailPageNotFound />;
  }

  // At this point, we know data is not null (we passed the early return)
  // Reassign non-null versions for use below
  const contactNN = contact!;
  const contactTeamNN = contactTeam!;
  const contactAuthedUserNN = contactAuthedUser!;
  const contactOrgNN = contactOrg;

  // Build action section data (uses non-null contact)
  const actionAtVariables = { ...fakeAtVariables, ...svrExtractAtVariables(userTeam, contactNN, contactOrgNN) };

  const actionSectionData: ActionSectionData = {
    userTeam: {
      userId: authUserId,
      teamId: authTeamId,
      unipileAccountId: userTeam.unipileAccountId,
      isSuperAdmin: userTeam.isSuperAdmin || isImpersonating,
      isPremium: userTeam.isPremium,
      mailType: userTeam.mailType,
      sendMessageType: userTeam.sendMessageType,
      scheduleIsWithinSchedule: userTeam.scheduleIsWithinSchedule,
      scheduleNextWindowStartFormatted: userTeam.scheduleNextWindowStartFormatted,
    },
    cadenceId: activeCadence?.cadence.id,
    // Only surface the pending AI step banner to super admins who are impersonating —
    // regular users shouldn't see "Scheduled message soon" for AI-drafted sends.
    pendingStep: isImpersonating ? (actionSectionQuery.data?.pendingStep ?? null) : null,
    scheduleStatus: actionSectionQuery.data?.scheduleStatus ?? null,
    linkedinSteps: LINKEDIN_STEPS,
    otherSteps: OTHER_STEPS,
    currentAction: q.action,
    isAddingToTakeAction: aiStepBulkCreateMutation.isPending,
    isDeletingPendingStep: deletePendingStepMutation.isPending,
    isUnipileConnecting: unipileAuthMutation.isPending,
    isSubmitting: false,
    atVariables: actionAtVariables,
    superAdminAiPreview:
      userTeam.isSuperAdmin || isImpersonating
        ? {
            onGenerate: () => {
              superAdminAiPreviewMutation.mutate({
                teamId: authTeamId,
                userId: authUserId,
                contactId: contactNN.id,
              });
            },
            isGenerating: superAdminAiPreviewMutation.isPending,
            prompt: superAdminAiPreviewMutation.data?.prompt ?? superAdminPromptQuery.data?.prompt ?? null,
            message: superAdminAiPreviewMutation.data?.body1 ?? null,
            error: superAdminAiPreviewMutation.error?.message ?? superAdminPromptQuery.error?.message ?? null,
            instructions: takeActionMessageInstructionsLocal,
            onChangeInstructions: (value: string) => {
              setTakeActionMessageInstructionsLocal(value);
              void debouncedSaveTakeActionMessageInstructions(value);
            },
            shouldSend: superAdminAiPreviewMutation.data?.shouldSend ?? null,
          }
        : undefined,
  };

  // Determine if step editor should show (for step editor rendering)
  const actionConnectedAt = contactAuthedUserNN.liConnectedAt ?? null;
  const actionProfessionalEmail = contactNN.fields?.professionalEmail?.find(
    (f: { primary: boolean }) => f.primary,
  )?.data;
  const actionDisabledState: Partial<Record<CadenceStepType, boolean>> = {
    email: !actionProfessionalEmail,
    follow: false,
    connect: actionConnectedAt != null,
    inmail: actionConnectedAt != null,
    message: actionConnectedAt == null,
    reminder: true,
    voice_note: false,
  };
  const actionPreventOpenForm = actionEditingStep?.type ? actionDisabledState[actionEditingStep.type] : false;

  // Campaign button callbacks
  const onCampaignEnroll = (templateId: string) => {
    // Switch option (namespaced id): route the contact into the switch instead
    // of a direct cadence enroll. The switch researches then routes by attribute.
    if (templateId.startsWith('switch:')) {
      const switchId = templateId.slice('switch:'.length);
      const switchName = enrollOptions.find((t) => t.id === templateId)?.name ?? 'switch';
      return updateWithToast({
        loading: `Adding to ${switchName}...`,
        success: `Added to ${switchName} — routing runs after research`,
        mutationFnAsync: () =>
          intakeIntoSwitchMutation.mutateAsync({
            teamId: userTeam.teamId,
            switchId,
            contactIds: [contactNN.id],
          }),
      });
    }

    // Sequence option (namespaced id): enroll the contact into the graph-based
    // journey. `assign` seats them at the entry node and starts the walk within
    // seconds — no research/staging step, so the toast reads "enrolled".
    if (templateId.startsWith('sequence:')) {
      const sequenceId = templateId.slice('sequence:'.length);
      const sequenceName = enrollOptions.find((t) => t.id === templateId)?.name ?? 'sequence';
      return updateWithToast({
        loading: `Adding to ${sequenceName}...`,
        success: `Enrolled in ${sequenceName}`,
        mutationFnAsync: () =>
          assignToSequenceMutation.mutateAsync({
            teamId: userTeam.teamId,
            sequenceId,
            contactIds: [contactNN.id],
          }),
      });
    }

    const templateName = enrollOptions.find((t) => t.id === templateId)?.name ?? 'campaign';
    // Off-state users skip staging entirely — the server enrolls directly, so
    // the toast must read "enrolled" not "researching" to match what they see.
    const success = userTeam.campaignPrefilterEnabled
      ? `Added to ${templateName} — researching`
      : `Enrolled in ${templateName}`;
    return updateWithToast({
      loading: `Adding to ${templateName}...`,
      success,
      mutationFnAsync: () =>
        createCadenceMutation.mutateAsync({ cadenceTemplateId: templateId, contactId: contactNN.id }),
    });
  };

  // Remove the contact from a sequence (the "in sequence X" pill's trash). Clears the enroll block.
  const onSequenceUnenroll = (sequencePublicId: string) => {
    const sequenceName = activeSequences.find((s) => s.sequencePublicId === sequencePublicId)?.name ?? 'sequence';
    return updateWithToast({
      loading: `Removing from ${sequenceName}...`,
      success: `Removed from ${sequenceName}`,
      mutationFnAsync: () =>
        removeFromSequenceMutation.mutateAsync({
          teamId: userTeam.teamId,
          sequenceId: sequencePublicId,
          contactIds: [contactNN.id],
        }),
    });
  };

  // Staging candidates → CampaignButton badges before active cadence promotion.
  // Filters to the shape the shared component expects and routes a click to the
  // template's staging page with the candidate's status pre-selected.
  const stagingCandidates: StagingCandidate[] = (stagingCandidatesData?.rows ?? [])
    .filter(
      (r): r is typeof r & { status: StagingCandidate['status'] } =>
        r.status === 'pending' ||
        r.status === 'researching' ||
        r.status === 'research_failed' ||
        r.status === 'gathering_sources' ||
        r.status === 'synthesizing' ||
        r.status === 'synthesize_failed' ||
        r.status === 'filter_failed' ||
        r.status === 'personalizing' ||
        r.status === 'personalize_failed' ||
        r.status === 'rate_limited' ||
        r.status === 'account_disconnected' ||
        r.status === 'awaiting_capacity' ||
        r.status === 'retrying' ||
        r.status === 'pending_review' ||
        r.status === 'rejected',
    )
    .flatMap((r): StagingCandidate[] => {
      // Prefer the resolved destination template; fall back to the switch for an
      // unresolved switch candidate (a switch is a campaign too, so it renders
      // as a badge). Drop the row only if it has neither — shouldn't happen for
      // an active staging row, but keeps the mapping total.
      const campaign =
        r.cadence_template_public_id != null
          ? {
              campaignKind: 'template' as const,
              campaignId: r.cadence_template_public_id,
              campaignName: r.cadence_template_name ?? 'Campaign',
            }
          : r.switch_public_id != null
            ? {
                campaignKind: 'switch' as const,
                campaignId: r.switch_public_id,
                campaignName: r.switch_name ?? 'Switch',
              }
            : null;
      if (campaign == null) return [];
      return [
        {
          candidateId: r.id,
          status: r.status,
          ...campaign,
          failureReason: r.failure_reason,
          userId: r.user_id,
          userName: r.user_name,
          userProfilePicture: r.user_profile_picture,
        },
      ];
    });

  const onStagingClick = (candidate: StagingCandidate) => {
    if (candidate.campaignKind === 'switch') {
      void router.push(`/switches/${candidate.campaignId}?switchDisplay=candidates`);
      return;
    }
    void router.push(`/templates/${candidate.campaignId}?templateDisplay=staging&candidateStatus=${candidate.status}`);
  };

  const candidateRemoveMutation = api.cadenceTemplate.candidateRemove.useMutation({
    onSuccess: async () => {
      await utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId });
    },
  });

  // Switch candidates have no template, so removing them goes through the switch
  // route instead of cadenceTemplate.candidateRemove. Also refresh the switch
  // page's counts/staging list so they don't go stale when acting from here.
  const switchCandidateRemoveMutation = api.campaignSwitch.candidateRemoveBulk.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId }),
        utils.campaignSwitch.candidateCounts.invalidate(),
        utils.campaignSwitch.stagingList.invalidate(),
      ]);
    },
  });

  const onStagingDelete = (candidate: StagingCandidate) =>
    updateWithToast({
      loading: `Removing from ${candidate.campaignName}...`,
      success: `Removed from ${candidate.campaignName}`,
      mutationFnAsync: async () => {
        if (candidate.campaignKind === 'switch') {
          await switchCandidateRemoveMutation.mutateAsync({
            teamId: authTeamId,
            switchId: candidate.campaignId,
            candidateIds: [candidate.candidateId],
          });
          return;
        }
        await candidateRemoveMutation.mutateAsync({
          cadenceTemplateId: candidate.campaignId,
          candidateId: candidate.candidateId,
        });
      },
    });

  // Reassign a staging candidate to a teammate before it's promoted to a
  // cadence — same affordance the active-cadence badge already has.
  const candidateChangeOwnerMutation = api.cadenceTemplate.candidateChangeOwner.useMutation({
    onSuccess: async () => {
      await utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId });
    },
  });

  // Owner reassignment only applies to template candidates — CampaignButtonWithCadence
  // keeps the switch badge's owner avatar read-only, so this is never invoked for a
  // switch candidate.
  const onStagingChangeOwner = (candidate: StagingCandidate, newUserId: string) =>
    updateWithToast({
      loading: 'Changing owner...',
      success: 'Owner changed',
      mutationFnAsync: () =>
        candidateChangeOwnerMutation.mutateAsync({
          cadenceTemplateId: candidate.campaignId,
          candidateId: candidate.candidateId,
          newUserId,
        }),
    });

  // Force-retry wiring. Reuses the existing candidateRetry mutation
  // (which resets status to `pending`, zeroes failure_type, and enqueues
  // a worker task). Surfaced on the inline candidate-in-progress card
  // so reps can unstick a gathering_sources row without waiting on the
  // 10-min stuck-row cron.
  const candidateRetryMutation = api.cadenceTemplate.candidateRetry.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId }),
        utils.cadenceTemplate.candidateCounts.invalidate(),
        utils.cadenceTemplate.candidateList.invalidate(),
      ]);
    },
  });
  // Switch candidates retry through the switch route (there's no per-template row).
  const switchCandidateRetryMutation = api.campaignSwitch.candidateRetryBulk.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.contactDetailBootstrap.secondaryForContact.invalidate({ contactId }),
        utils.campaignSwitch.candidateCounts.invalidate(),
        utils.campaignSwitch.stagingList.invalidate(),
      ]);
    },
  });
  const onStagingRetry = (candidate: StagingCandidate) =>
    updateWithToast({
      loading: `Retrying ${candidate.campaignName}...`,
      success: 'Re-queued for research',
      mutationFnAsync: async () => {
        if (candidate.campaignKind === 'switch') {
          await switchCandidateRetryMutation.mutateAsync({
            teamId: authTeamId,
            switchId: candidate.campaignId,
            candidateIds: [candidate.candidateId],
          });
          return;
        }
        await candidateRetryMutation.mutateAsync({
          cadenceTemplateId: candidate.campaignId,
          candidateId: candidate.candidateId,
        });
      },
    });

  const onCampaignPause = (cadenceId: string) =>
    updateWithToast({
      loading: 'Pausing...',
      success: 'Campaign paused',
      mutationFnAsync: () => pauseMutation.mutateAsync({ cadenceIds: [cadenceId] }),
    });

  const onCampaignResume = (cadenceId: string) =>
    updateWithToast({
      loading: 'Resuming...',
      success: 'Campaign resumed',
      mutationFnAsync: () => resumeMutation.mutateAsync({ cadenceIds: [cadenceId], stepIds: [] }),
    });

  const onCampaignReattach = (cadenceId: string) =>
    updateWithToast({
      loading: 'Re-attaching...',
      success: 'Campaign re-attached',
      mutationFnAsync: () =>
        reattachMutation.mutateAsync({ teamId: contactNN.teamId, cadenceId, params: { state: 'active' } }),
    });

  const onCampaignDelete = (cadenceId: string) =>
    updateWithToast({
      loading: 'Deleting campaign...',
      success: 'Campaign deleted',
      mutationFnAsync: () => deleteCadenceMutation.mutateAsync({ cadenceId }),
      onAfterSuccess: () => {
        // Strip cadenceId from URL so contact.byId refetches
        const ids = splitIds(q.id ?? '');
        if (ids.cadenceId) {
          updateQuery({ id: joinIds({ ...ids, cadenceId: undefined }) });
        }
      },
    });

  const onCampaignChangeOwner = (cadenceId: string, newUserId: string) =>
    updateWithToast({
      loading: 'Changing owner...',
      success: 'Owner changed',
      mutationFnAsync: () => changeOwnerMutation.mutateAsync({ newUserId, teamId: contactNN.teamId, cadenceId }),
    });

  // Switch the detail view to a different active campaign by re-pinning its cadenceId
  // in the URL — contact.byId refetches and drives the chip + steps for that campaign.
  // Always carry the resolved contactId so a missing/short id can't drop it.
  const onSelectCampaign = (cadenceId: string) => {
    if (cadenceId === activeCadence?.cadence.id) return;
    updateQuery({ id: joinIds({ ...splitIds(q.id ?? ''), contactId, cadenceId }) });
  };

  const campaignIsPending =
    pauseMutation.isPending || resumeMutation.isPending || reattachMutation.isPending || changeOwnerMutation.isPending;

  // ============================================================================
  // ContactDataSection Callbacks
  // ============================================================================

  const onToggleTracking = (tracked: boolean) =>
    updateWithToast({
      loading: tracked ? 'Enabling tracking...' : 'Disabling tracking...',
      success: tracked ? 'Contact tracking enabled' : 'Contact tracking disabled',
      mutationFnAsync: () => trackingMutation.mutateAsync({ contactId, data: { tracked } }),
    });

  const onToggleDisqualified = (disqualified: boolean) =>
    updateWithToast({
      loading: disqualified ? 'Disqualifying...' : 'Re-qualifying...',
      success: disqualified ? 'Contact disqualified' : 'Contact re-qualified',
      mutationFnAsync: () => disqualifiedMutation.mutateAsync({ contactId, disqualified }),
    });

  const onAddFieldValue = (name: PureContactFieldName, value: string, source: string) =>
    updateWithToast({
      loading: 'Adding',
      success: 'Added',
      mutationFnAsync: () =>
        updateContact.mutateAsync({
          contactId,
          data: {
            [name]: value,
            source,
            primary: true,
          },
        }),
    });

  const onDeleteFieldValue = (field: PureContactFieldName, source: string) =>
    updateWithToast({
      loading: 'Deleting',
      success: 'Deleted',
      mutationFnAsync: () =>
        removeValue.mutateAsync({
          contactId,
          field: field as ContactFieldName,
          source,
        }),
    });

  const onAssignPhoneToOrg = (source: string) =>
    updateWithToast({
      loading: 'Moving to organization…',
      success: 'Moved to organization',
      mutationFnAsync: () => reassignPhoneToOrg.mutateAsync({ contactId, source }),
    });

  const onMakeFieldPrimary = async (field: PureContactFieldName, source: string) => {
    const fieldCheck = contactPrimarySettableFieldKeySchema.safeParse(field);
    if (!fieldCheck.success) {
      toast.error("this field can't be made primary");
      return;
    }

    await updateWithToast({
      loading: 'Making Primary',
      success: 'Is now primary',
      mutationFnAsync: () =>
        makePrimary.mutateAsync({
          contactId,
          data: { field: fieldCheck.data, source },
        }),
    });
  };

  const onRemoveFieldPrimary = async (field: PureContactFieldName, source: string) => {
    const fieldCheck = contactPrimarySettableFieldKeySchema.safeParse(field);
    if (!fieldCheck.success) {
      toast.error("this field can't be removed primary");
      return;
    }

    await updateWithToast({
      loading: 'Removing Primary',
      success: 'Primary removed',
      mutationFnAsync: () =>
        removePrimary.mutateAsync({
          contactId,
          data: { field: fieldCheck.data, source },
        }),
    });
  };

  const onVerifyEmail = async (field: PureContactFieldName, source: string) => {
    const fieldCheck = contactEmailFieldKeySchema.safeParse(field);
    if (!fieldCheck.success) {
      toast.error("this field can't be verified");
      return;
    }

    await updateWithToast({
      loading: 'Verifying email...',
      success: 'Email verified successfully',
      mutationFnAsync: async () => {
        const res = await verifyEmail.mutateAsync({
          contactId,
          data: {
            field: fieldCheck.data,
            source,
          },
        });
        if (res.verified === false) {
          const email = res.value;
          toast(email ? `${email} is verified to be invalid` : 'The email is not valid', {
            icon: '✉️',
          });
        }
      },
    });
  };

  const onMarkPhoneFirmNumber = async (phone: string) => {
    await updateWithToast({
      loading: 'Marking as firm number...',
      success: 'Marked as firm number',
      mutationFnAsync: () =>
        markPhoneFirmNumber.mutateAsync({
          contactId,
          phone,
        }),
    });
  };

  const onRefreshFromLinkedIn = () =>
    updateWithToast({
      loading: 'Refreshing from LinkedIn...',
      success: 'Contact info refreshed from LinkedIn',
      mutationFnAsync: () =>
        researchMutation.mutateAsync({
          contactId,
          teamId: authTeamId,
          userId: authUserId,
        }),
    });

  const onEnhanceContact = async (): Promise<{ deleted: boolean }> => {
    const toastId = toast.loading('Enriching contact...');
    try {
      const res = await enhanceMutation.mutateAsync({ contactId });
      const { deleted } = res;
      if (deleted) {
        // The verify step confirmed this LinkedIn profile no longer exists and can't be
        // repaired, so Enhance removed the contact server-side in the same call. Report
        // the removal; Core closes the now-gone detail view.
        toast('Contact removed — this LinkedIn profile no longer exists on LinkedIn.', {
          id: toastId,
          icon: '🗑️',
          duration: 6000,
        });
        return { deleted: true };
      }
      // The queue gate is NON-THROWING: a skip must be said out loud, not
      // silently toasted as success (that read as "the feature is broken").
      // The only skip left is a concurrent run — the 24h cooldown is gone
      // (enrichment freshness is the oracle's job; re-runs served from its
      // cache are free).
      if (res.enrichQueued === false) {
        toast('Enrichment is already running for this contact.', { id: toastId, icon: '⏳', duration: 6000 });
        return { deleted: false };
      }
      toast.success('Enrichment queued — results will appear automatically.', { id: toastId });
      return { deleted: false };
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to enrich contact', { id: toastId });
      return { deleted: false };
    }
  };

  const onDeleteMeetingBooked = async (interactionId: number) => {
    await deleteMeetingMutation.mutateAsync({ contactId, interactionId });
  };

  const onAgeOverride = (minAge: number | null) =>
    updateWithToast({
      loading: minAge != null ? 'Updating age estimate...' : 'Resetting age estimate...',
      success: minAge != null ? 'Age estimate updated' : 'Age estimate reset to calculated value',
      mutationFnAsync: () => overrideMutation.mutateAsync({ contactId, minAge }),
    });

  const onResetGender = () =>
    updateWithToast({
      loading: 'Resetting gender...',
      success: 'Gender and title reset',
      mutationFnAsync: () => resetGenderMutation.mutateAsync({ contactId }),
    });

  const onDeleteLinkedInMapping = (mappingId: number) =>
    updateWithToast({
      loading: 'Deleting identifier...',
      success: 'Identifier deleted',
      mutationFnAsync: () => deleteForeignMappingMutation.mutateAsync({ contactId, mappingId }),
    });

  const onVerifyLinkedInMapping = async (mappingId: number) => {
    try {
      const result = await verifyForeignMappingMutation.mutateAsync({ contactId, mappingId });
      if (result.success === true) {
        const messages = [result.profileName ? `Verified: ${result.profileName}` : 'Identifier verified'];
        if (result.addedIdentifier) {
          messages.push(
            `Added: ${result.addedIdentifier.length > 20 ? `${result.addedIdentifier.slice(0, 10)}...` : result.addedIdentifier}`,
          );
        }
        toast.success(messages.join('\n'));
      } else {
        const errorMsg = 'error' in result ? result.error : 'Verification failed';
        const markedUnavailable = 'markedUnavailable' in result && result.markedUnavailable;
        if (markedUnavailable) {
          toast.error(`${errorMsg}\n\nAll identifiers failed - contact marked as unavailable.`);
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to verify identifier');
    }
  };

  const onAddLinkedInMapping = async (foreignSource: 'linkedin' | 'salesnav', foreignId: string) => {
    try {
      const result = await addForeignMappingMutation.mutateAsync({
        contactId,
        foreignSource,
        foreignId,
      });
      toast.success(result.reactivated ? 'Identifier added and contact reactivated' : 'Identifier added');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add identifier');
    }
  };

  const onCopyLinkedInMapping = (foreignId: string) => {
    void navigator.clipboard.writeText(foreignId);
    toast.success('Copied to clipboard');
  };

  // Build mutations object for ContactDetailPageCore
  const coreMutations: ContactDetailMutations = {
    updateNotes: onDebouncedNotesChange,
    setContactStage: (stageId) => {
      void contactSetStageMutation.mutateAsync({ contactId: contactNN.id, stageId });
      return Promise.resolve();
    },
    setOrgStage: (stageId) => {
      if (contactOrgNN?.id) orgSetStageMutation.mutate({ orgId: contactOrgNN.id, stageId });
      return Promise.resolve();
    },
    updateField: onAddFieldValue,
    deleteFieldValue: onDeleteFieldValue,
    assignPhoneToOrg: onAssignPhoneToOrg,
    makeFieldPrimary: onMakeFieldPrimary,
    removeFieldPrimary: onRemoveFieldPrimary,
    verifyEmail: onVerifyEmail,
    markPhoneFirmNumber: onMarkPhoneFirmNumber,
    setTracking: onToggleTracking,
    setDisqualified: onToggleDisqualified,
    ackResponse: (contactId, autoPause) => ackContentViewAndResponseMutation.mutateAsync({ contactId, autoPause }),
    sendEmail: (contactId, emailData) => sendEmailMutation.mutateAsync({ contactId, data: emailData }),
    refreshFromLinkedIn: onRefreshFromLinkedIn,
    enhanceContact: onEnhanceContact,
    deleteMeetingBooked: onDeleteMeetingBooked,
    setAgeOverride: onAgeOverride,
    resetGender: onResetGender,
    isSettingContactStage: contactSetStageMutation.isPending,
    isSettingOrgStage: orgSetStageMutation.isPending,
    isRefreshing: researchMutation.isPending,
    isEnhancing: enhanceMutation.isPending,
    isTrackingPending: trackingMutation.isPending,
    isDisqualifiedPending: disqualifiedMutation.isPending,
    isAgeOverridePending: overrideMutation.isPending,
    isResetGenderPending: resetGenderMutation.isPending,
    enrollInCampaign: onCampaignEnroll,
    pauseCadence: onCampaignPause,
    resumeCadence: onCampaignResume,
    reattachCadence: onCampaignReattach,
    deleteCadence: onCampaignDelete,
    changeCadenceOwner: onCampaignChangeOwner,
    openCampaignTemplate: (templateId) => void router.push(`/templates/${templateId}`),
    unenrollSequence: onSequenceUnenroll,
    isSequenceUnenrollingId: removeFromSequenceMutation.isPending
      ? (removeFromSequenceMutation.variables?.sequenceId ?? null)
      : null,
    isCampaignPending: campaignIsPending,
    isCampaignDeleting: deleteCadenceMutation.isPending,
    markMeetingBooked: (id) => markMeetingBookedMutation.mutate({ interactionPublicId: id }),
    retryAttachment: handleRetryAttachment,
    isMarkingMeetingBooked: markMeetingBookedMutation.isPending,
  };

  const handleCopyToMyTeam = async () => {
    if (!authData.impersonation) return;
    try {
      const result = await updateWithToastPromise({
        loading: 'Copying contact...',
        success: 'Done!',
        mutationFnAsync: () =>
          copyToMyTeamMutation.mutateAsync({
            contactPublicId: contactNN.id,
          }),
      });
      if (result.alreadyExisted) {
        toast.success(`Contact already exists in "${result.targetTeamName}"`, { duration: 5000 });
      } else {
        toast.success(`Contact copied to "${result.targetTeamName}"`, { duration: 5000 });
      }
    } catch {
      // Error already shown by updateWithToastPromise
    }
  };

  const handleDeleteContact = async () => {
    if (!(authIsSuperadmin || isImpersonating)) return;
    try {
      await updateWithToastPromise({
        loading: 'Deleting contact...',
        success: 'Contact deleted',
        mutationFnAsync: () => deleteContactMutation.mutateAsync({ contactId: contactNN.id }),
      });
      onClose?.();
    } catch {
      // Error already shown by updateWithToastPromise
    }
  };

  const onClickToRespond = (type: string) => {
    const baseSvrStep: Step = {
      id: '',
      type: 'message',
      subject: null,
      body2: null,
      to: null,
      from: null,
      cc: null,
      bcc: null,
      onlyOpenLink: false,
      useEmailSignature: false,
    };
    if (type === 'inmail_received') {
      setRespondStep({ ...baseSvrStep, type: 'inmail', subject: '', body2: '' });
      updateQuery({ action: type });
    } else if (type === 'email_received') {
      setRespondStep({ ...baseSvrStep, type: 'email', subject: '', body2: '' });
      updateQuery({ action: type });
    } else if (type === 'message_received') {
      setRespondStep({ ...baseSvrStep, type: 'message', body2: '' });
      updateQuery({ action: type });
    } else if (type === 'document_viewed') {
      setRespondStep({ ...baseSvrStep, type: 'message', body2: '' });
      updateQuery({ action: type });
    }
  };

  // Handler for toggling InMail open/all mode
  const handleToggleInmailOpen = async (nodeId: string, onlyOpenLink: boolean) => {
    await handleCadenceUpdateStep(nodeId, { onlyOpenLink });
  };

  const onCancel = () => {
    setRespondStep(undefined);
    setRespondChanges({});
    setRespondAiChoices([]);
    setRespondAiIndex(0);
    setRespondAiCommand('');
    localStorage.removeItem('form');
  };

  // Respond section AI generation handler
  const handleRespondGenerateAi = async (empty = false) => {
    if (!respondStep) return;
    const stepType = respondChanges.type ?? respondStep.type;
    if (!['email', 'inmail', 'connect', 'message'].includes(stepType)) return;

    try {
      const messageChoices = await assistMutation.mutateAsync({
        contactId: contactNN.id,
        stepType: stepType as 'email' | 'inmail' | 'connect' | 'message',
        command: respondAiCommand.length > 0 ? respondAiCommand : undefined,
        partialBody: empty ? undefined : getTextFromHtml(respondChanges.body2 ?? respondStep.body2 ?? ''),
      });

      const choices = messageChoices.map((choice) => ({
        subject: choice.subject,
        body: choice.body.replaceAll('\\n\\n', '<div><br/></div>'),
      }));

      setRespondAiChoices(choices);
      setRespondAiIndex(0);

      const firstChoice = choices[0];
      if (firstChoice) {
        setRespondChanges((current) => ({
          ...current,
          body2: firstChoice.body,
          subject: firstChoice.subject,
        }));
      }
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
      } else {
        toast.error('Failed to generate a message');
      }
    }
  };

  // Handle respond step changes
  const handleRespondChange = (data: StepChanges) => {
    setRespondChanges((current) => ({ ...current, ...data }));
  };

  // Handle respond step toast
  const handleRespondToast = (message: string, type: 'error' | 'success') => {
    if (type === 'error') {
      toast.error(message);
    } else {
      toast.success(message);
    }
  };

  const onSubmit = async (step: CadenceStepInput) => {
    await ackContentViewAndResponseMutation.mutateAsync({ contactId: contactNN.id });
    try {
      if (step.type === 'email' && userTeam?.mailType === 'gmail-api') {
        const body = step.body2;
        const subject = step.subject;
        if (body == null) {
          Toast.error({ title: 'Error', message: 'Email must have a body', duration: 4000 });
          return;
        }
        if (subject == null) {
          Toast.error({ title: 'Error', message: 'Email must have a subject', duration: 4000 });
          return;
        }
        await sendEmailMutation.mutateAsync({ contactId: contactNN.id, data: { subject, body } });
        Toast.success({ title: 'Sent email', message: 'Yay! The email was sent successfully.', duration: 4000 });
        onCancel();
        setRespondStep(undefined);
        updateQuery({ action: undefined });
        return;
      }
      // Email without Gmail — no direct send channel. Block with a toast and keep
      // the respond composer open (draft preserved) instead of silently closing.
      if (step.type === 'email') {
        const noChannel = 'No email channel connected. Connect Gmail to send email.';
        toast.error(noChannel, { duration: 5000 });
        return;
      }
      if ((step.type === 'message' || step.type === 'inmail') && hasUnipile) {
        if (!step.body2) {
          toast.error('Message content is required');
          return;
        }
        const messageResult = await sendMessageViaMutation
          .mutateAsync({
            contactId: contactNN.id,
            teamId: authTeamId,
            userId: authUserId,
            text: step.body2,
            isInmail: step.type === 'inmail',
            subject: step.type === 'inmail' ? (step.subject ?? undefined) : undefined,
          })
          .catch((e: unknown) => {
            const errorMessage = isTRPCClientError(e)
              ? e.message
              : e instanceof Error
                ? e.message
                : 'Failed to send message';
            toast.error(errorMessage, { duration: 5000 });
            return null;
          });
        if (messageResult) {
          toast.success(step.type === 'inmail' ? 'InMail sent' : 'Message sent');
          if (activeCadence?.cadence)
            await deleteFutureStepsMutation.mutateAsync({ cadenceId: activeCadence.cadence.id });
          onCancel();
        } else {
          return;
        }
      } else if (step.type === 'connect' && hasUnipile) {
        const result = await sendConnectionViaMutation
          .mutateAsync({
            contactId: contactNN.id,
            teamId: authTeamId,
            userId: authUserId,
            message: step.body2 || undefined,
          })
          .catch((e: unknown) => {
            const errorMessage = isTRPCClientError(e)
              ? e.message
              : e instanceof Error
                ? e.message
                : 'Failed to send connection request';
            toast.error(errorMessage, { duration: 5000 });
            return null;
          });
        if (result) {
          toast.success('Connection request sent');
          onCancel();
        } else {
          return;
        }
      } else if (step.type === 'message' || step.type === 'inmail' || step.type === 'connect') {
        // No Unipile connection — block with a toast and keep the respond
        // composer open (draft preserved) instead of scheduling a legacy step.
        const message = 'Unipile is not connected. Please connect your LinkedIn account first.';
        toast.error(message, { duration: 5000 });
        return;
      }
      setRespondStep(undefined);
      updateQuery({ action: undefined });
    } catch (e: unknown) {
      if (e instanceof Error) toast.error(e.message || 'Failed to submit.', { duration: 5000 });
    }
  };

  // Handle respond step submit (wraps onSubmit with loading state)
  const handleRespondSubmit = async (finalStepData: StepData) => {
    setRespondIsSubmitting(true);
    try {
      await onSubmit({
        delay: 0,
        type: finalStepData.type,
        subject: finalStepData.subject ?? undefined,
        body2: finalStepData.body2 ?? undefined,
        from: finalStepData.from ?? undefined,
        to: finalStepData.to ?? undefined,
        cc: finalStepData.cc ?? undefined,
        bcc: finalStepData.bcc ?? undefined,
      });
    } finally {
      setRespondIsSubmitting(false);
    }
  };

  // ============================================================================
  // Cadence Step Handlers (for CadenceStepsPure)
  // ============================================================================
  const handleCadenceCreateStep = async (stepData: CadenceAddStepV2Params) => {
    if (!activeCadence) return;
    await updateWithToast({
      loading: 'Adding Step',
      success: 'Step added',
      mutationFnAsync: () =>
        cadenceCreateStepMutation.mutateAsync({ cadenceId: activeCadence.cadence.id, data: stepData }),
    });
  };

  const handleCadenceUpdateStep = async (stepId: string, stepData: CadenceUpdateStepParams) => {
    await updateWithToast({
      loading: 'Updating Step',
      success: 'Step updated',
      onAfterSuccess: () => setUpdatedStepId(stepId),
      mutationFnAsync: () => cadenceUpdateStepMutation.mutateAsync({ stepId, data: stepData }),
    });
  };

  const handleCadenceChangeType = async (stepId: string, type: Exclude<CadenceStepType, 'wait_for_connect'>) => {
    await updateWithToast({
      loading: 'Changing Step Type',
      success: 'Step type changed',
      mutationFnAsync: () => cadenceUpdateStepMutation.mutateAsync({ stepId, data: { type } }),
    });
  };

  const handleCadenceDeleteNode = async (stepId: string) => {
    await updateWithToast({
      loading: 'Deleting Step',
      success: 'Step deleted',
      mutationFnAsync: () => cadenceDeleteStepMutation.mutateAsync({ stepId }),
    });
  };

  const handleCadenceMarkFinished = async (stepId: string) => {
    await updateWithToast({
      loading: 'Marking finished',
      success: 'Step marked as finished',
      mutationFnAsync: () => cadenceChangeStepStateMutation.mutateAsync({ ids: [stepId], state: 'finished' }),
    });
  };

  // View campaigns callbacks
  const onViewAllCampaigns = () => {
    setCampaignIdFromHistory(null);
    setShowCampaigns(true);
  };

  const onClickCadence = (cadenceId: string) => {
    setCampaignIdFromHistory(cadenceId);
    // Set the selection directly here instead of mirroring campaignIdFromHistory
    // through a useEffect (which added an extra render hop).
    setSelectedCampaignId(cadenceId);
    setShowCampaigns(true);
  };

  const onBackFromCampaigns = () => {
    setShowCampaigns(false);
    setCampaignIdFromHistory(null);
  };

  // View required actions callbacks
  const onViewRequiredActions = () => {
    setShowRequiredActions(true);
  };

  const onBackFromRequiredActions = () => {
    setShowRequiredActions(false);
  };

  // Render required actions section overlay
  if (showRequiredActions && contact) {
    return <RequiredActionsListView contactId={contact.id} onBack={onBackFromRequiredActions} />;
  }

  // Render campaigns section overlay
  if (showCampaigns && contact) {
    // Use custom back handler if viewing detail, otherwise return to list
    const handleBackFromDetail = campaignIdFromHistory ? onBackFromCampaigns : undefined;

    if (selectedCampaignId) {
      return (
        <CampaignDetailView
          contactId={contact.id}
          campaignId={selectedCampaignId}
          currentUserId={userTeam.userId}
          onBack={handleBackFromDetail ?? (() => setSelectedCampaignId(null))}
        />
      );
    }

    return (
      <CampaignListView contactId={contact.id} onSelectCampaign={setSelectedCampaignId} onBack={onBackFromCampaigns} />
    );
  }

  const orgSearchForExperience = async (query: string) => {
    const res = await utils.org.search.fetch({
      teamId: userTeam.teamId,
      query: query.length > 0 ? query : undefined,
      pagination: { offset: 0, limit: 20 },
    });
    return res.orgs.map((o) => ({ id: o.id, name: o.name ?? 'Unnamed organization', pictureUrl: o.pictureUrl }));
  };

  const handleAddExperience: ContactDetailPageCoreProps['onAddExperience'] = async (input) => {
    await updateWithToast({
      loading: 'Adding experience',
      success: 'Experience added',
      mutationFnAsync: async () => {
        await createExperienceMutation.mutateAsync({
          contactId,
          orgId: input.orgId,
          startMonth: input.startMonth,
          startYear: input.startYear,
          endMonth: input.endMonth,
          endYear: input.endYear,
          current: input.current,
        });
      },
    });
  };

  const handleDeleteExperience: ContactDetailPageCoreProps['onDeleteExperience'] = async (experienceId) => {
    await updateWithToast({
      loading: 'Removing experience',
      success: 'Experience removed',
      mutationFnAsync: async () => {
        await deleteExperienceMutation.mutateAsync({ contactId, experienceId });
      },
    });
  };

  return (
    <ContactDetailPageCore
      enrichmentInFlight={enrichInFlightIds.has(contactId)}
      contactId={contactId}
      authTeamId={userTeam.teamId}
      authTeamName={userTeam.teamName}
      authUserId={userTeam.userId}
      authUnipileAccountId={userTeam.unipileAccountId ?? undefined}
      authUnipileAccountError={userTeam.unipileAccountError}
      authIsPremium={userTeam.isPremium}
      authCreditsRemaining={userTeam.creditsRemaining}
      // Cast data since tRPC types are compatible but not identical to Core types
      data={data as unknown as CoreContactDetailData}
      isLoading={false}
      stages={detailBootstrapQuery.data?.stages.stages ?? []}
      stagesLoading={detailBootstrapQuery.isLoading}
      history={mapHistoryData(historyQuery.data?.data)}
      historyIsLoading={historyQuery.isPending}
      meetingBookedDetails={detailBootstrapQuery.data?.meetingBookedDetails ?? null}
      pendingAction={detailBootstrapQuery.data?.pendingAction ?? null}
      teamUsers={detailBootstrapQuery.data?.teamUsers.teamUsers?.map((u) => ({
        userId: u.userId,
        profilePicture: u.profilePicture,
        userTeamName: u.userTeamName,
        userEmail: u.userEmail,
      }))}
      activeTab={defaultId}
      onTabChange={(detailType) => updateQuery({ detailType })}
      interactDisplay={q.interactDisplay}
      onInteractDisplayChange={(interactDisplay) => updateQuery({ interactDisplay })}
      mutations={coreMutations}
      honorificAgeThreshold={userTeam.honorificAgeThreshold}
      voiceEnabled={userTeam.voiceEnabled}
      voiceCallingEnabled={userTeam.voiceCallingEnabled}
      onClose={onClose}
      onClickOrg={
        contactOrgNN
          ? () =>
              setQuery((q) => ({
                ...q,
                orgId: contactOrgNN.id,
                detailType: 'interact',
                ...(orgFirst ? { id: undefined } : {}),
              }))
          : undefined
      }
      contactTeamId={contactTeamNN.id}
      contactTeamName={contactTeamNN.name}
      onCancelRespond={onCancel}
      onClickToRespond={onClickToRespond}
      updatedStepId={updatedStepId}
      setUpdatedStepId={setUpdatedStepId}
      retryStates={retryStates}
      retryErrors={retryErrors}
      onRetryAttachment={handleRetryAttachment}
      transcriptionRequestStates={transcriptionRequestStates}
      onRequestTranscription={handleRequestTranscription}
      atVariables={atVariables}
      orgFirst={orgFirst}
      onFieldCopied={(d) => toast.success(`${d} copied to clipboard`)}
      onFieldCopyFailed={(d) => toast.error(`Failed to copy ${d}`)}
      linkedInMappings={detailBootstrapQuery.data?.foreignMappings.mappings ?? []}
      linkedinUnavailable={detailBootstrapQuery.data?.foreignMappings.linkedinUnavailable}
      isLinkedInMappingsLoading={detailBootstrapQuery.isLoading}
      onDeleteLinkedInMapping={onDeleteLinkedInMapping}
      onVerifyLinkedInMapping={onVerifyLinkedInMapping}
      onCopyLinkedInMapping={onCopyLinkedInMapping}
      onAddLinkedInMapping={onAddLinkedInMapping}
      isLinkedInMappingDeleting={deleteForeignMappingMutation.isPending}
      isLinkedInMappingVerifying={verifyForeignMappingMutation.isPending}
      isLinkedInMappingAdding={addForeignMappingMutation.isPending}
      renderContactTags={() => <ContactTagEditor contactId={contactNN.id} teamId={authTeamId} />}
      signals={signals}
      isSignalsLoading={isSignalsLoading}
      lastUnipileFetchAt={lastUnipileFetchAt}
      ignoredAspectKeys={ignoredAspectKeys}
      dossier={detailBootstrapQuery.data?.dossier ?? null}
      isDossierGenerating={detailBootstrapQuery.data?.isGenerating ?? false}
      // Show the dossier section when the team is opted into the
      // staging/candidates flow (which generates dossiers automatically) OR
      // when this contact already has one / is getting one — dossiers can now
      // also come from the contacts-list "Generate dossiers" bulk action, and
      // a generated dossier the user can't see would read as a bug.
      hideDossierTab={
        userTeam.campaignPrefilterEnabled !== true &&
        detailBootstrapQuery.data?.dossier == null &&
        detailBootstrapQuery.data?.isGenerating !== true
      }
      onRegenerateDossier={() => {
        const hasExistingDossier = detailBootstrapQuery.data?.dossier != null;
        return updateWithToast({
          loading: hasExistingDossier ? 'Regenerating dossier…' : 'Generating dossier…',
          success: hasExistingDossier ? 'Dossier updated.' : 'Dossier generated.',
          mutationFnAsync: async () => {
            const res = await regenerateDossierMutation.mutateAsync({
              userId: userTeam.userId,
              teamId: userTeam.teamId,
              contactId: contactNN.id,
            });
            // Throw on failure so updateWithToast surfaces the detailed reason.
            if (!res.ok) {
              throw new Error(res.failure.reason);
            }
          },
        });
      }}
      isRegeneratingDossier={regenerateDossierMutation.isPending}
      experiences={detailBootstrapQuery.data?.experienceAndEducation.experiences ?? []}
      education={detailBootstrapQuery.data?.experienceAndEducation.education ?? []}
      isHistoryLoading={detailBootstrapQuery.isLoading}
      onAddExperience={handleAddExperience}
      orgSearch={orgSearchForExperience}
      onDeleteExperience={handleDeleteExperience}
      onViewAllCampaigns={onViewAllCampaigns}
      onViewRequiredActions={onViewRequiredActions}
      onClickCadence={onClickCadence}
      campaigns={activeCampaigns}
      selectedCampaignId={activeCadence?.cadence.id ?? null}
      onSelectCampaign={onSelectCampaign}
      onCopyToSuperadminTeam={isImpersonating ? handleCopyToMyTeam : undefined}
      isCopyPending={copyToMyTeamMutation.isPending}
      contactImages={showSignalsForSuperadmin ? (contactImagesQuery.data ?? null) : undefined}
      isContactImagesLoading={contactImagesQuery.isLoading}
      onDeleteContact={authIsSuperadmin || isImpersonating ? handleDeleteContact : undefined}
      isDeletingContact={deleteContactMutation.isPending}
      superadminName={
        authData.impersonation?.superadminName ??
        getAuth().currentUser?.displayName ??
        getAuth().currentUser?.email ??
        undefined
      }
      superadminTeamName={authData.impersonation?.superadminTeamName}
      campaignTemplates={enrollOptions}
      campaignOwners={campaignOwners}
      lastUsedTemplateId={userTeam.lastUsedCadenceTemplateId}
      lastUsedTemplateName={userTeam.lastUsedCadenceTemplateName}
      activeSequences={activeSequences}
      stagingCandidates={stagingCandidates}
      onStagingClick={onStagingClick}
      onStagingDelete={onStagingDelete}
      onStagingChangeOwner={onStagingChangeOwner}
      onStagingRetry={onStagingRetry}
      stagingRetryingCandidateId={
        candidateRetryMutation.isPending ? (candidateRetryMutation.variables?.candidateId ?? null) : null
      }
      isStagingDeletingCandidateId={
        candidateRemoveMutation.isPending ? (candidateRemoveMutation.variables?.candidateId ?? null) : null
      }
      // Action section data and callbacks
      actionSectionData={actionSectionData}
      onActionChange={handleActionChange}
      onSubmitActionStep={async (step, sendMode) => {
        // The sendMode from the step editor needs to be used
        setActionSendMode(sendMode);
        await handleSubmitActionStep(
          {
            type: step.type,
            subject: step.subject ?? undefined,
            body2: step.body2 ?? undefined,
            from: step.from ?? undefined,
            to: step.to ?? undefined,
            cc: step.cc ?? undefined,
            bcc: step.bcc ?? undefined,
            useEmailSignature: step.useEmailSignature,
            delay: 0,
          },
          sendMode, // Pass the sendMode directly from the step editor
        );
      }}
      onAddToTakeAction={handleAddToTakeAction}
      addToTakeActionOnlyNewPeople={userTeam.takeActionOnlyNewPeople}
      onChangeAddToTakeActionOnlyNewPeople={handleChangeAddToTakeActionOnlyNewPeople}
      onDeletePendingStep={handleDeletePendingStep}
      getInitialActionStep={getInitialActionStep}
      onUnipileConnect={handleUnipileConnect}
      // Action card props
      isMacLike={isMacLike()}
      tel={contact?.primaryFields?.phone?.data}
      clickToCall={
        userTeam.voiceCallingEnabled && userTeam.voiceCallerNumber ? (
          <ClickToCallButton
            teamId={authTeamId}
            contactId={contactId}
            contactName={
              [contact?.primaryFields?.firstName?.data, contact?.primaryFields?.lastName?.data]
                .filter(Boolean)
                .join(' ') || undefined
            }
            phoneNumber={contact?.primaryFields?.phone?.data}
            orgPhoneNumber={contactOrg?.primaryFields?.phone?.data}
            imageSrc={contact?.primaryPictureUrl}
            provider={userTeam.voiceProvider}
            enabled
            callingConnected
          />
        ) : undefined
      }
      // "Schedule a call" renders as its own row below the reminder card. Scheduling doesn't need a
      // caller number (you're not dialing yet), so it shows whenever voice calling is enabled.
      scheduleCall={
        userTeam.voiceCallingEnabled ? (
          <ScheduleCallButton
            teamId={authTeamId}
            contactId={contactId}
            contactName={
              [contact?.primaryFields?.firstName?.data, contact?.primaryFields?.lastName?.data]
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
        ) : undefined
      }
      // Dialer for a triggered cadence `call` step's task card. Unlike the profile
      // click-to-call above, this stays visible (greyed) when the rep has no caller number so
      // the rep can see the pending call; it's hidden only when the team flag is off.
      renderCadenceCallButton={({ cadenceStepId }) => (
        <ClickToCallButton
          teamId={authTeamId}
          contactId={contactId}
          contactName={
            [contact?.primaryFields?.firstName?.data, contact?.primaryFields?.lastName?.data]
              .filter(Boolean)
              .join(' ') || undefined
          }
          phoneNumber={contact?.primaryFields?.phone?.data}
          orgPhoneNumber={contactOrg?.primaryFields?.phone?.data}
          imageSrc={contact?.primaryPictureUrl}
          provider={userTeam.voiceProvider}
          enabled={userTeam.voiceCallingEnabled}
          callingConnected={Boolean(userTeam.voiceCallerNumber)}
          cadenceStepId={cadenceStepId}
          onCallLogged={() => {
            // The logged outcome finished the step server-side (deleting its reminder and
            // advancing the cadence) — refresh the reminder card + cadence graph.
            void utils.contactDetailBootstrap.forContact.invalidate({ teamId: authTeamId, contactId });
            void utils.contact.byId.invalidate({ contactId });
            void actionSectionQuery.refetch();
          }}
        />
      )}
      onMarkCallStepDone={handleMarkCallStepDone}
      // Dialer for a standalone scheduled `call` reminder's card. Same click-to-call as the profile
      // button, but wired to the reminder id so logging the outcome clears the scheduled call.
      renderReminderCallButton={
        userTeam.voiceCallingEnabled
          ? ({ globalReminderId }) => (
              <ClickToCallButton
                teamId={authTeamId}
                contactId={contactId}
                contactName={
                  [contact?.primaryFields?.firstName?.data, contact?.primaryFields?.lastName?.data]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                phoneNumber={contact?.primaryFields?.phone?.data}
                orgPhoneNumber={contactOrg?.primaryFields?.phone?.data}
                imageSrc={contact?.primaryPictureUrl}
                provider={userTeam.voiceProvider}
                enabled={userTeam.voiceCallingEnabled}
                callingConnected={Boolean(userTeam.voiceCallerNumber)}
                globalReminderId={globalReminderId}
                onCallLogged={() => {
                  // The logged outcome cleared the scheduled call server-side — refresh the
                  // reminder card + calls queue.
                  void utils.contactDetailBootstrap.forContact.invalidate({ teamId: authTeamId, contactId });
                  void utils.contact.byId.invalidate({ contactId });
                  void actionSectionQuery.refetch();
                }}
              />
            )
          : undefined
      }
      onRecordManualCall={handleRecordManualCall}
      reminderCardData={reminderCardData}
      reminderCardCallbacks={reminderCardCallbacks}
      voiceNoteCardData={voiceNoteCardData}
      voiceNoteCardCallbacks={voiceNoteCardCallbacks}
      messageAttachment={{
        filename: messageAttachment?.filename ?? null,
        onSelectFile: (file) => void onSelectMessageAttachmentFile(file),
        onRemove: onRemoveMessageAttachment,
        isUploading: isUploadingMessageAttachment,
      }}
      // Step editor data (pure approach - renders CadenceStepSectionPure directly)
      stepEditorData={!actionPreventOpenForm ? stepEditorData : undefined}
      stepEditorCallbacks={!actionPreventOpenForm ? stepEditorCallbacks : undefined}
      autoGenerateAi={actionAutoGenerateAi}
      onAutoGenerateAiChange={setActionAutoGenerateAi}
      // Cadence section data
      cadenceStepsGraph={cadenceStepsGraph}
      cadenceTemplateInfo={cadenceTemplateInfo}
      cadenceDocuments={svrDocuments?.map((d) => ({ id: d.id, title: d.title, url: d.url }))}
      // Cadence section callbacks (use type assertions to bridge pure types with tRPC types)
      onCadenceCreateStep={
        handleCadenceCreateStep as (data: {
          type: string;
          body2?: string | null;
          subject?: string | null;
          delay?: number;
          prevStepId?: string | null;
        }) => unknown
      }
      onCadenceUpdateStep={
        handleCadenceUpdateStep as (
          stepId: string,
          data: { type?: string; body2?: string | null; subject?: string | null; delay?: number },
        ) => unknown
      }
      onCadenceDeleteStep={handleCadenceDeleteNode}
      onCadenceChangeStepType={handleCadenceChangeType as (stepId: string, type: string) => Promise<void>}
      onCadenceMarkStepFinished={handleCadenceMarkFinished}
      onToggleInmailOpen={handleToggleInmailOpen}
      // AI Message Section removed with smart campaigns
      aiMessageData={aiMessageData}
      // Respond section data props
      respondStep={respondStepData}
      respondDocuments={stepEditorDocuments}
      respondAiChoices={respondAiChoices}
      respondAiIndex={respondAiIndex}
      respondAiCommand={respondAiCommand}
      respondAiLoading={assistMutation.isPending}
      respondStepsList={gatedCadenceStepsList}
      respondIsSubmitting={respondIsSubmitting}
      respondIsAiGenerating={assistMutation.isPending}
      onRespondSubmit={handleRespondSubmit}
      onRespondChange={handleRespondChange}
      onRespondGenerateAi={handleRespondGenerateAi}
      onRespondAiCommandChange={setRespondAiCommand}
      onRespondAiIndexChange={setRespondAiIndex}
      onRespondToast={handleRespondToast}
      renderSiftConditionBuilder={(props) => (
        // Compact, sidebar-friendly variant — stacks Field / Condition /
        // Value vertically inside each condition card so nothing clips.
        // SiftConfig from @poseidon/types is structurally compatible with
        // the card's local SiftConfig — one cast at the bridge keeps both
        // ends honest.
        <SiftConditionBuilderCompact
          value={props.value as Parameters<typeof SiftConditionBuilderCompact>[0]['value']}
          onChange={(v) => props.onChange(v as typeof props.value)}
        />
      )}
      // Touch campaigns replace the vertical cadence timeline with the same
      // editable, branching node-canvas the template page shows. Only passed
      // when the active cadence's template is a touch campaign (else the linear
      // timeline renders). The canvas opens in Minimal (compact icon nodes) with
      // a Minimal / Expanded toggle; both modes show the full branching graph.
      renderTouchCampaignGraph={
        touchCampaignTemplate != null
          ? () => (
              <ContactTouchGraphPanel
                editorContext={touchEditorContext}
                // Prefer the contact's instance graph (their real steps + personalized
                // content); fall back to the template graph only when the instance can't
                // be built. `instanceEdit` routes content saves to the contact's own
                // cadence_step (not the template) and hides create/delete/restructure.
                cadenceTemplate={touchCampaignInstance ?? touchCampaignTemplate}
                contactStepStatuses={contactStepStatuses}
                onMarkStepFinished={handleCadenceMarkFinished}
                instanceEdit={touchCampaignInstance != null ? { updateStep: handleCadenceUpdateStep } : undefined}
              />
            )
          : undefined
      }
      // Per-contact sequence graph — the locked touch-sequence node canvas for every sequence this contact
      // is actively enrolled in. Gated on the `sequenceEnabled` flag + at least one active enrollment, so the
      // interact tab shows nothing sequence-related for teams / contacts without one.
      renderSequenceGraph={
        userTeam.sequenceEnabled === true && activeSequences.length > 0
          ? () => (
              <ContactSequenceGraph
                teamId={userTeam.teamId}
                contactId={contactId}
                variables={sequenceEditorVariables}
                documents={sequenceEditorDocuments}
              />
            )
          : undefined
      }
      reminderActions={detailBootstrapQuery.data?.reminderActions.reminderActions ?? []}
      reminderActionsLoading={detailBootstrapQuery.isLoading}
      onCreateReminder={async (name) => {
        // useCreateReminderMutation already invalidates reminderAction.all
        // on success so the dropdown picks up the new option automatically.
        await createReminderMutation.mutateAsync({
          teamId: authTeamId,
          data: { name },
        });
      }}
      reminderCreating={createReminderMutation.isPending}
      onReminderError={(message) => toast.error(message)}
    />
  );
};
